import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Checkbox,
  Chip,
} from "@mui/material";
import {
  IconButton,
  Grid,
  Divider,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  LinearProgress,
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import {
  CloudUpload,
  Visibility,
  Description,
  Brightness4,
  Brightness7,
  LocalHospital,
  Search,
  Refresh,
  Logout as LogoutIcon,
  Person,
  Settings,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Close,
  Send,
  Edit,
  Info as InfoIcon,
  Business,
  DoneAll,
  History as HistoryIcon,
  Science as ScienceIcon,
  Schedule,
  AssignmentInd,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { useTheme, createTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadImage } from "../../services/images";
import EnhancedUploadDialog from "./EnhancedUploadDialog";
import ChangePasswordDialog from "../Auth/ChangePassword";
import NotificationListener from "../Common/NotificationListener";
import {
  getUnreadNotifications,
  markAllNotificationsAsRead,
} from "../../services/notifications";
import CreateSelfReport from "./CreateSelfReport";
import RequestDycovueReader from "./RequestDycovueReader";
import CreateOrder from "./CreateOrder";
import SendEmailDialog from "./SendEmailDialog";
import EditPatientMetadataDialog from "./EditPatientMetadataDialog";
import MessageHistory from "./MessageHistory";
import ScanBoard from "./ScanBoard";
import RISWorklist from "./RISWorklist";
import SchedulingBoard from "./SchedulingBoard";
import RoomManagement from "../Admin/RoomManagement";
import Imaging from "./Imaging";
import ReportsList from "./ReportsList";
import PatientRegistration from "./PatientRegistration";
import PatientProfileView from "./PatientProfileView";
import ReportStatusBadge from "../Common/ReportStatusBadge";
import ReportViewer from "../Common/ReportViewer";
import UserManagement from "../Admin/UserManagement";
import PatientHistoryPanel from "../Common/PatientHistoryPanel";
import { getStudyStatistics } from "../../services/analytics";
import { getScanBoardStudies, syncOrthanc } from "../../services/pacs";
import { patientService, orderService } from "../../services/ris";
import {
  isTodayDate,
  resolveWorkflowStatusLabel,
  WORKFLOW_STATUS_COLORS,
} from "./workflowStatus";
import FullLogo, { IconLogo } from "../logo/FullLogo";
import ProfileSettings from "../Admin/ProfileSettings";
import UserSettings from "../UserSettings";
import HelpSupport from "../Admin/HelpSupport";
import RequestLabTestDialog from "../Radiologist/RequestLabTestDialog";
import LabReportModal from "../Radiologist/LabReportModal";
import { formatDicomDate, generateOHIFUrl } from "../../services/utils";

// Helper function to format DICOM patient names (replace ^ separator with space)
const formatPatientName = (name) => {
  if (!name) return "Unknown Patient";
  return name.replace(/\^/g, " ").trim() || "Unknown Patient";
};

// Tesla-inspired color palette
const teslaColors = {
  dark: {
    primary: "#3E6AE1",
    secondary: "#34C3FF",
    background: "#121212",
    surface: "#1E1E1E",
    card: "rgba(30, 30, 30, 0.8)",
    text: "#FFFFFF",
    textSecondary: "rgba(255, 255, 255, 0.7)",
    border: "rgba(255, 255, 255, 0.12)",
  },
  light: {
    primary: "#3E6AE1",
    secondary: "#34C3FF",
    background: "#F5F5F7",
    surface: "#FFFFFF",
    card: "rgba(255, 255, 255, 0.8)",
    text: "#121212",
    textSecondary: "rgba(18, 18, 18, 0.7)",
    border: "rgba(0, 0, 0, 0.12)",
  },
};

// Styled icon button with modern effects
const ModernIconButton = styled(IconButton)(({
  theme,
  variant = "default",
}) => {
  const gradients = {
    view: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    report: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    selfReport: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    inProgress: "linear-gradient(135deg, #f6a623 0%, #ffb74d 100%)",
    dycovue: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    email: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    edit: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
    info: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)",
    disabled: "linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)",
  };

  // Solid fallback colors (one per variant) used to show color by default
  const solidColors = {
    view: "#667eea",
    report: "#f5576c",
    selfReport: "#4facfe",
    inProgress: "#f6a623",
    dycovue: "#43e97b",
    email: "#fa709a",
    edit: "#30cfd0",
    info: "#2193b0",
    disabled: "#95a5a6",
  };

  const gradient = gradients[variant] || gradients.view;
  const solid = solidColors[variant] || solidColors.view;

  return {
    position: "relative",
    padding: "3px",
    width: 26,
    height: 26,
    borderRadius: "6px",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.04)",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: "all 0.2s ease",
    "& .MuiSvgIcon-root": {
      fontSize: "15px",
      color: solid,
      transition: "all 0.2s ease",
    },
    "&:hover": {
      background: gradient,
      transform: "scale(1.08)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      border: `1px solid ${alpha("#fff", 0.2)}`,
      "& .MuiSvgIcon-root": {
        WebkitTextFillColor: "#fff",
        color: "#fff",
        transform: "scale(1.05)",
      },
    },
    "&:active": {
      transform: "scale(0.96)",
    },
    "&.Mui-disabled": {
      backgroundColor: alpha(theme.palette.action.disabledBackground, 0.3),
      border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
      "& .MuiSvgIcon-root": {
        color: solidColors.disabled,
      },
    },
  };
});

// Performant surface styled components
const GlassPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
  borderRadius: "12px",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.08)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.25)"
      : "0 2px 8px rgba(0, 0, 0, 0.05)",
  backgroundImage: "none",
}));

const GlowButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
  border: `2px solid ${teslaColors.dark.primary}`,
  borderRadius: "12px",
  color: "white",
  padding: "10px 24px",
  boxShadow: `0 0 15px ${alpha(teslaColors.dark.primary, 0.5)}`,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: `0 0 20px ${alpha(teslaColors.dark.primary, 0.8)}`,
    transform: "translateY(-2px)",
    border: `2px solid ${teslaColors.dark.secondary}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    fontSize: "0.875rem",
  },
}));

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : collapsedDrawerWidth,
    height: "100%",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    background: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
    borderRight: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#E2E8F0"}`,
    display: "flex",
    flexDirection: "column",
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "isDark",
})(({ theme, selected, isDark }) => {
  const dark = theme.palette.mode === "dark";
  const inactiveColor = dark ? "rgba(255, 255, 255, 0.7)" : "#64748B";
  const hoverTextColor = dark ? "#FFFFFF" : "#0F172A";
  const selectedBg = dark ? "rgba(13, 148, 136, 0.18)" : "#F0FDFA";
  const hoverBg = dark ? "rgba(255, 255, 255, 0.06)" : "#F8FAFC";

  return {
    borderRadius: "12px",
    margin: "4px 12px",
    marginBottom: "8px",
    padding: "12px 16px",
    transition: "all 0.2s ease",
    color: selected ? "#0D9488" : inactiveColor,
    backgroundColor: selected ? selectedBg : "transparent",
    "& .MuiListItemIcon-root": {
      color: selected ? "#0D9488" : inactiveColor,
      transition: "color 0.2s ease",
      minWidth: "36px",
    },
    "&:hover": {
      backgroundColor: selected ? selectedBg : hoverBg,
      color: selected ? "#2DD4BF" : hoverTextColor,
      "& .MuiListItemIcon-root": {
        color: selected ? "#2DD4BF" : hoverTextColor,
      },
    },
  };
});
// NDPA 2023 s.26 — compute consent display state for a patient record
const getConsentDisplay = (p) => {
  const c = p && p.consent;
  if (!c || c.consentGiven !== true) {
    return { label: 'Not Captured', color: '#B91C1C', bg: '#FEF2F2' };
  }
  if (c.withdrawn) {
    return { label: 'Withdrawn', color: '#B45309', bg: '#FEF3C7' };
  }
  return { label: 'Consented', color: '#047857', bg: '#ECFDF5' };
};

// Small consent badge used across the RIS patient detail views
const ConsentBadge = ({ patient, isDark }) => {
  const cs = getConsentDisplay(patient);
  const title = patient && patient.consent
    ? `Consent ${cs.label}${patient.consent.withdrawnReason ? ` — ${patient.consent.withdrawnReason}` : ''}`
    : 'Consent not captured';
  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.9,
        py: '2px',
        borderRadius: '6px',
        fontSize: '0.7rem',
        fontWeight: 700,
        bgcolor: isDark ? 'rgba(255,255,255,0.08)' : cs.bg,
        color: cs.color,
      }}
    >
      {cs.label}
    </Box>
  );
};

// Patient Detail Dialog Component
const PatientDetailDialog = ({ open, onClose, study, onEditPatient, onRequestLabTest }) => {
  const theme = useTheme();
  const [patientRegistryInfo, setPatientRegistryInfo] = React.useState(null);
  const [loadingPatient, setLoadingPatient] = React.useState(false);

  React.useEffect(() => {
    if (open && study && (study.patientId || study.patient?.patientId)) {
      const mrn = study.patientId || study.patient?.patientId;
      setLoadingPatient(true);
      patientService
        .getByMrn(mrn)
        .then((res) => {
          if (res.data && res.data.patient) {
            setPatientRegistryInfo(res.data.patient);
          } else {
            setPatientRegistryInfo(null);
          }
        })
        .catch((err) => {
          console.warn("Could not fetch patient registry details:", err);
          setPatientRegistryInfo(null);
        })
        .finally(() => {
          setLoadingPatient(false);
        });
    } else {
      setPatientRegistryInfo(null);
    }
  }, [open, study]);

  if (!study) return null;

  const patient = study.patient || {};
  const displayPhone =
    patientRegistryInfo?.telephone ||
    patientRegistryInfo?.phone ||
    patient.telephone ||
    study.telephone ||
    "-";
  const displayEmail =
    patientRegistryInfo?.email || patient.email || study.email || "-";
  const displayAddress =
    patientRegistryInfo?.address || patient.address || study.address || "-";
  const displaySsn =
    patientRegistryInfo?.ssn || patient.ssn || study.ssn || "-";

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: { xs: '95vw', sm: '760px', md: '780px' },
          maxWidth: '800px !important',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.98rem' }}>Patient & Study Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
        <Grid container spacing={1.5}>
          {/* Patient Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Patient Information
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Patient Name
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {formatPatientName(patient.patientName || study.patientName)}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Patient ID
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {patient.patientId || study.patientId || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Date of Birth
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {patient.patientBirthDate || study.patientBirthDate
                ? formatDicomDate(
                    patient.patientBirthDate || study.patientBirthDate,
                  )
                : "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Sex
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {(patient.patientSex || study.patientSex) === "M"
                ? "Male"
                : (patient.patientSex || study.patientSex) === "F"
                  ? "Female"
                  : (patient.patientSex || study.patientSex) === "O"
                    ? "Other"
                    : patient.patientSex || study.patientSex || "Unknown"}
            </Typography>
          </Grid>

          {(displayPhone !== "-" || displayEmail !== "-" || displayAddress !== "-") && (
            <>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
                  Phone Number
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
                  {loadingPatient ? <CircularProgress size={14} /> : displayPhone}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
                  Email Address
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem', wordBreak: 'break-all' }}>
                  {loadingPatient ? <CircularProgress size={14} /> : displayEmail}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
                  Address
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
                  {loadingPatient ? <CircularProgress size={14} /> : displayAddress}
                </Typography>
              </Grid>
            </>
          )}

          {/* NDPA 2023 s.26 — consent status for this patient */}
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Data Protection Consent
            </Typography>
            <Box sx={{ mt: 0.25 }}>
              {loadingPatient ? (
                <CircularProgress size={14} />
              ) : (
                <ConsentBadge patient={patientRegistryInfo || patient} isDark={theme.palette.mode === 'dark'} />
              )}
            </Box>
          </Grid>

          {/* Study Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ fontSize: '0.85rem', mt: 0.5 }}>
              Study Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Study Description
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.studyDescription || "Unnamed Study"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Modality
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.modality || "Unknown"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Study Date & Time
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.studyDate
                ? new Date(study.studyDate).toLocaleDateString()
                : "N/A"}{study.studyTime && study.studyTime !== "N/A" ? ` • ${study.studyTime}` : ""}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Accession Number
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.accessionNumber || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Referring Physician
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.referringPhysicianName || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Series / Images
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.seriesCount ?? study.totalSeries ?? 0} series • {study.totalInstances ?? study.instanceCount ?? 0} images
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Report Status
            </Typography>
            <Box sx={{ mt: 0.25, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {study.selfReportStatus || study.dycovueReportStatus ? (
                <>
                  {study.selfReportStatus && (
                    <ReportStatusBadge
                      status={study.selfReportStatus}
                      type="self-report"
                    />
                  )}
                  {study.dycovueReportStatus && (
                    <ReportStatusBadge
                      status={study.dycovueReportStatus}
                      type="dycovue-report"
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" fontWeight={500} color="textSecondary" sx={{ fontSize: '0.82rem' }}>
                  None
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Technical Details: Study Instance UID */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Study Instance UID:
              </Typography>
              <Typography
                variant="caption"
                fontWeight={500}
                sx={{
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: "textSecondary",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                {study.studyInstanceUID || "N/A"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ScienceIcon />}
          onClick={() => {
            if (onRequestLabTest) {
              onRequestLabTest(study);
            }
          }}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontFamily: '"Satoshi", sans-serif',
            fontWeight: 700,
            color: "#7C3AED",
            borderColor: "#7C3AED",
            "&:hover": { borderColor: "#6D28D9", bgcolor: "rgba(124, 58, 237, 0.08)" },
          }}
        >
          Request Lab Test
        </Button>
        <Box>
        {onEditPatient && (patientRegistryInfo || study.patient?._id || (study._id && study.isPatientOnly)) && (
          <Button
            onClick={() => {
              onClose();
              const editTarget = patientRegistryInfo || (study.patient?._id ? study.patient : null) || study;
              onEditPatient(editTarget);
            }}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontFamily: '"Satoshi", sans-serif',
              fontWeight: 700,
              borderColor: "#0D9488",
              color: "#0D9488",
              "&:hover": { borderColor: "#00665C", bgcolor: "#F0FDFA" },
              mr: 1,
            }}
          >
            Edit Patient Details
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontFamily: '"Satoshi", sans-serif',
            fontWeight: 700,
            bgcolor: "#00665C",
            color: "#FFF",
            "&:hover": { bgcolor: "#004D47" },
          }}
        >
          Close
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Create theme function
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          primary: { main: teslaColors.light.primary },
          secondary: { main: teslaColors.light.secondary },
          background: {
            default: teslaColors.light.background,
            paper: teslaColors.light.surface,
          },
          text: {
            primary: teslaColors.light.text,
            secondary: teslaColors.light.textSecondary,
          },
          divider: teslaColors.light.border,
        }
      : {
          primary: { main: teslaColors.dark.primary },
          secondary: { main: teslaColors.dark.secondary },
          background: {
            default: teslaColors.dark.background,
            paper: teslaColors.dark.surface,
          },
          text: {
            primary: teslaColors.dark.text,
            secondary: teslaColors.dark.textSecondary,
          },
          divider: teslaColors.dark.border,
        }),
  },
  typography: {
    fontFamily:
      '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h6: { fontWeight: 600 },
    body1: { fontWeight: 400, lineHeight: 1.5 },
    body2: { fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", borderRadius: "12px", fontWeight: 500 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
        },
      },
    },
  },
});

// Color mode context
export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

// Simple debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ─── Status tag helpers ──────────────────────────────────────────────────────

const MODALITY_COLORS = {
  CT:  { color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
  MR:  { color: '#c084fc', bg: 'rgba(192,132,252,0.1)' },
  MRI: { color: '#c084fc', bg: 'rgba(192,132,252,0.1)' },
  XR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  XRAY: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  CR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  DR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  US:  { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)' },
  MG:  { color: '#fb923c', bg: 'rgba(251,146,60,0.1)' },
  MAMMO: { color: '#fb923c', bg: 'rgba(251,146,60,0.1)' },
  PT:  { color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  NM:  { color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  OT:  { color: '#334155', bg: 'rgba(148,163,184,0.18)' },
};

const getModalityPalette = (modality) => {
  const key = String(modality || '').trim().toUpperCase();
  return MODALITY_COLORS[key] || MODALITY_COLORS.OT;
};

const ORDER_WORKFLOW_CHIPS = {
  NEW:      { label: 'New',       color: '#94a3b8', bg: 'rgba(148,163,184,0.13)' },
  ARRIVAL:  { label: 'Arrived',   color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
  READY:    { label: 'Ready',     color: '#22d3ee', bg: 'rgba(34,211,238,0.12)' },
  STARTED:  { label: 'Scanning',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  ENDEXAM:  { label: 'Scanned',   color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  QC:       { label: 'QC',        color: '#c084fc', bg: 'rgba(192,132,252,0.12)' },
  TOREPORT: { label: 'To Report', color: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
  PRELIM:   { label: 'Prelim',    color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
};

const TAG = {
  display: 'inline-flex',
  alignItems: 'center',
  px: '5px',
  py: '1.5px',
  borderRadius: '4px',
  fontSize: '0.58rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  lineHeight: 1.5,
};

function getStudyStatusChips(study) {
  const chips = [];
  if (study.isOrder) {
    chips.push({ label: 'Order', color: '#818cf8', bg: 'rgba(129,140,248,0.15)' });
    const wf = ORDER_WORKFLOW_CHIPS[study.orderStatus || study.status];
    if (wf) chips.push(wf);
    if (study.hasImages || (study.totalInstances || 0) > 0) {
      chips.push({ label: `${study.totalInstances || '?'} img`, color: '#4ade80', bg: 'rgba(74,222,128,0.1)' });
    } else {
      chips.push({ label: 'No Images', color: '#64748b', bg: 'rgba(100,116,139,0.1)' });
    }
  } else {
    if (study.dycovueReportStatus === 'completed') {
      chips.push({ label: 'Reported ✓', color: '#4ade80', bg: 'rgba(74,222,128,0.12)' });
    } else if (study.dycovueReportStatus === 'in_progress') {
      chips.push({ label: 'Reporting', color: '#fb923c', bg: 'rgba(251,146,60,0.12)' });
    } else if (study.dycovueReportStatus === 'claimed') {
      chips.push({ label: 'Claimed', color: '#c084fc', bg: 'rgba(192,132,252,0.12)' });
    } else if (study.dycovueReportStatus === 'requested') {
      chips.push({ label: 'Requested', color: '#818cf8', bg: 'rgba(129,140,248,0.12)' });
    }
    if (study.selfReportStatus === 'completed') {
      chips.push({ label: 'Self-Report ✓', color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' });
    } else if (study.selfReportStatus === 'in_progress' || study.selfReportStatus === 'draft') {
      chips.push({ label: 'Draft', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' });
    }
    if ((study.totalInstances || 0) > 0) {
      chips.push({ label: `${study.totalInstances} img`, color: '#4ade80', bg: 'rgba(74,222,128,0.1)' });
    }
  }
  return chips;
}

// ─────────────────────────────────────────────────────────────────────────────

// Memoized table row
const StudyRow = React.memo(function StudyRow({
  studyData,
  index,
  t,
  isMobile,
  formatPatientName,
  onViewImages,
  onViewReports,
  onCreateSelfReport,
  onRequestDycovue,
  onSendEmail,
  onEditMetadata,
  onViewDetails,
  onViewHistory,
  onRequestLabTest,
  isSelected = false,
  onToggleSelect,
}) {
  const { patient, ...study } = studyData;
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const isSelfInProgress =
    study.selfReportStatus === "in_progress" ||
    study.selfReportStatus === "draft";

  const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
  const isClaimed = Boolean(
    ['claimed', 'in_progress'].includes(study.dycovueReportStatus) ||
    assignedId ||
    study.orderStatus === 'claimed'
  );
  const isCompleted = study.dycovueReportStatus === 'completed' || study.selfReportStatus === 'completed';
  const isLockedClaimed = isClaimed && !isCompleted;

  // Self-Report Button Status
  const getSelfReportStyle = () => {
    if (study.selfReportStatus === "completed") {
      return {
        background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        borderColor: "#4facfe",
        color: "white",
      };
    } else if (isSelfInProgress) {
      return {
        background: "linear-gradient(135deg, #f6a623 0%, #ffb74d 100%)",
        borderColor: "#f6a623",
        color: "white",
        animation: "pulse-orange 2s infinite",
        "@keyframes pulse-orange": {
          "0%, 100%": { boxShadow: "0 0 0 0 rgba(246, 166, 35, 0.7)" },
          "50%": { boxShadow: "0 0 0 8px rgba(246, 166, 35, 0)" },
        },
      };
    } else {
      return {
        background: alpha("#4facfe", 0.2),
        borderColor: alpha("#4facfe", 0.4),
        color: "#4facfe",
      };
    }
  };

  // DycoVue Button Status
  const getDycovueStyle = () => {
    if (study.dycovueReportStatus === "completed") {
      return {
        background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
        backgroundColor: "#43e97b",
        borderColor: "#43e97b",
        color: "white",
        animation: "pulse-green 2s infinite",
        "@keyframes pulse-green": {
          "0%, 100%": {
            boxShadow: "0 0 0 0 rgba(67, 233, 123, 0.7)",
            transform: "scale(1)",
          },
          "50%": {
            boxShadow: "0 0 0 10px rgba(67, 233, 123, 0)",
            transform: "scale(1.05)",
          },
        },
      };
    } else if (study.dycovueReportStatus === "in_progress") {
      return {
        background: "linear-gradient(135deg, #f6a623 0%, #ffb74d 100%)",
        backgroundColor: "#f6a623",
        borderColor: "#f6a623",
        color: "white",
      };
    } else if (study.dycovueReportStatus === "claimed") {
      return {
        background: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(99, 102, 241, 0.15)",
        backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(99, 102, 241, 0.15)",
        borderColor: "#6366f1",
        color: isDark ? "#a5b4fc" : "#4f46e5",
      };
    } else if (study.dycovueReportStatus === "requested") {
      return {
        background: isDark ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.15)",
        backgroundColor: isDark ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.15)",
        borderColor: "#f59e0b",
        color: isDark ? "#fbbf24" : "#d97706",
      };
    } else {
      return {
        background: isDark ? "rgba(67, 233, 123, 0.15)" : "rgba(67, 233, 123, 0.2)",
        backgroundColor: isDark ? "rgba(67, 233, 123, 0.15)" : "rgba(67, 233, 123, 0.2)",
        borderColor: isDark ? "rgba(67, 233, 123, 0.4)" : "#34d399",
        color: isDark ? "#43e97b" : "#059669",
      };
    }
  };

  // compute styles for this row so buttons can reference them
  const selfStyle = getSelfReportStyle();
  const dycoStyle = getDycovueStyle();
  const cardBorder = isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)";

  return (
    <TableRow
      key={`study-${index}-${study.studyInstanceUID || study._id}`}
      hover
      selected={isSelected}
      sx={study.isOrder ? { '& > td:first-of-type': { borderLeft: '3px solid rgba(129,140,248,0.5)' } } : { '& > td:first-of-type': { borderLeft: '3px solid transparent' } }}
    >
      {onToggleSelect && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ py: '4px !important', px: '6px !important', borderBottom: cardBorder }}>
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => onToggleSelect(studyData)}
          />
        </TableCell>
      )}
      <TableCell
        sx={{
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
        }}
      >
        <Box sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatPatientName(patient.patientName)}</Box>
        <Box sx={{ display: 'flex', gap: '3px', mt: '2px', flexWrap: 'wrap' }}>
          {getStudyStatusChips(study).map((chip, i) => (
            <Box key={i} component="span" sx={{ ...TAG, backgroundColor: chip.bg, color: chip.color, py: '1px', px: '5px', fontSize: '0.65rem' }}>
              {chip.label}
            </Box>
          ))}
        </Box>
      </TableCell>
      <TableCell
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
        }}
      >
        {patient.patientId}
      </TableCell>

      {/* Date of Birth Column - Hidden on mobile */}
      <TableCell
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
          display: { xs: "none", md: "table-cell" },
        }}
      >
        {patient.patientBirthDate
          ? formatDicomDate(patient.patientBirthDate)
          : "N/A"}
      </TableCell>

      {/* Gender Column - Hidden on extra small screens */}
      <TableCell
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
          display: { xs: "none", sm: "table-cell" },
        }}
      >
        {patient.patientSex === "M"
          ? "Male"
          : patient.patientSex === "F"
            ? "Female"
            : patient.patientSex === "O"
              ? "Other"
              : patient.patientSex || "Unknown"}
      </TableCell>

      <TableCell
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
        }}
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.78rem' }}>
          {study.studyDescription || t("dashboard.unnamedStudy")}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", py: '4px !important', px: '8px !important', borderBottom: cardBorder }}>
        {study.modality ? (
          <Box component="span" sx={{
            ...TAG,
            fontSize: '0.7rem',
            fontWeight: 800,
            px: '6px',
            py: '2px',
            borderRadius: '4px',
            backgroundColor: getModalityPalette(study.modality).bg,
            color: getModalityPalette(study.modality).color,
          }}>
            {study.modality}
          </Box>
        ) : <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>–</Box>}
      </TableCell>
      <TableCell
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          fontSize: "0.78rem",
          borderBottom: cardBorder,
        }}
      >
        {study.studyDate
          ? new Date(study.studyDate).toLocaleDateString()
          : "N/A"}
      </TableCell>
      <TableCell
        align="center"
        sx={{
          whiteSpace: "nowrap",
          py: '4px !important',
          px: '8px !important',
          borderBottom: cardBorder,
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="center"
          flexWrap="nowrap"
        >
          <Tooltip title={isLockedClaimed ? "Study Claimed by Radiologist — Locked (View Images Only)" : "View Patient Details"}>
            <span>
              <ModernIconButton
                variant="info"
                size="small"
                onClick={() => !isLockedClaimed && onViewDetails(studyData)}
                disabled={isLockedClaimed}
                sx={isLockedClaimed ? { opacity: 0.35, pointerEvents: 'none' } : {}}
              >
                <InfoIcon fontSize="small" />
              </ModernIconButton>
            </span>
          </Tooltip>

          <Tooltip title={isLockedClaimed ? "View Images in OHIF Viewer (Study Claimed)" : "View Images in OHIF Viewer (opens in new tab)"}>
            <span>
              <ModernIconButton
                variant="view"
                size="small"
                onClick={() => onViewImages(study)}
                disabled={!study.orthancStudyId && !study.studyInstanceUID}
              >
                <Visibility fontSize="small" />
              </ModernIconButton>
            </span>
          </Tooltip>

          {/* SELF-REPORT BUTTON - WITH VISIBLE COLORS */}
          <Tooltip
            title={
              isLockedClaimed
                ? "Study Claimed by Radiologist — Locked (Awaiting Radiologist Report)"
                : study.selfReportStatus === "completed"
                  ? "View Self-Report"
                  : isSelfInProgress
                    ? "Continue Self-Report (Draft)"
                    : "Create Self-Report"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (isLockedClaimed) return;
                  onCreateSelfReport(study);
                }}
                disabled={isLockedClaimed}
                sx={{
                  padding: "3px",
                  width: 26,
                  height: 26,
                  borderRadius: "6px",
                  border: "1px solid",
                  transition: "all 0.3s ease",
                  ...(isLockedClaimed ? {
                    opacity: 0.35,
                    pointerEvents: 'none',
                    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#64748B' : '#94A3B8'
                  } : selfStyle),
                  "& .MuiSvgIcon-root": {
                    fontSize: "15px",
                    color: isLockedClaimed ? (isDark ? '#64748B' : '#94A3B8') : (selfStyle.color || selfStyle.iconColor || "inherit"),
                    transition: "all 0.2s ease",
                  },
                  "&:hover": {
                    transform: isLockedClaimed ? "none" : "scale(1.08)",
                    boxShadow: isLockedClaimed ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
                    background: isLockedClaimed ? undefined : (selfStyle.background || selfStyle.bg || selfStyle["background"]),
                    borderColor: isLockedClaimed ? undefined : (selfStyle.borderColor || selfStyle.border || selfStyle["borderColor"]),
                    color: isLockedClaimed ? undefined : selfStyle.color,
                    "& .MuiSvgIcon-root": {
                      color: isLockedClaimed ? undefined : "#fff",
                      WebkitTextFillColor: isLockedClaimed ? undefined : "#fff",
                    },
                  },
                }}
              >
                <Description sx={{ fontSize: "15px" }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* DYCOVUE BUTTON - WITH VISIBLE COLORS */}
          <Tooltip
            title={
              study.dycovueReportStatus === "completed"
                ? "View DycoVue Report"
                : isLockedClaimed
                  ? "Claimed by Radiologist — Awaiting Report"
                  : study.dycovueReportStatus === "in_progress"
                    ? "Report In Progress"
                    : study.dycovueReportStatus === "claimed"
                      ? "Claimed by Radiologist"
                      : study.dycovueReportStatus === "requested"
                        ? "Requested - Awaiting Radiologist"
                        : "Request DycoVue Report"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (study.dycovueReportStatus === "completed") {
                    onViewReports(study);
                  } else if (
                    isLockedClaimed ||
                    study.dycovueReportStatus === "requested" ||
                    study.dycovueReportStatus === "claimed" ||
                    study.dycovueReportStatus === "in_progress"
                  ) {
                    return; // Do nothing - already requested/claimed
                  } else {
                    onRequestDycovue(study);
                  }
                }}
                disabled={
                  isLockedClaimed ||
                  study.dycovueReportStatus === "requested" ||
                  study.dycovueReportStatus === "claimed" ||
                  study.dycovueReportStatus === "in_progress"
                }
                sx={{
                  padding: "3px",
                  width: 26,
                  height: 26,
                  borderRadius: "6px",
                  border: "1px solid",
                  transition: "all 0.3s ease",
                  background: dycoStyle.background || dycoStyle.backgroundColor,
                  backgroundColor: dycoStyle.backgroundColor || dycoStyle.background,
                  borderColor: dycoStyle.borderColor,
                  color: dycoStyle.color,
                  "& .MuiSvgIcon-root": {
                    fontSize: "15px",
                    color: dycoStyle.color,
                    transition: "all 0.2s ease",
                  },
                  "&:hover": {
                    transform:
                      study.dycovueReportStatus === "requested"
                        ? "none"
                        : "scale(1.08)",
                    boxShadow:
                      study.dycovueReportStatus === "requested"
                        ? "none"
                        : "0 2px 8px rgba(0,0,0,0.15)",
                    background: dycoStyle.background,
                    borderColor: dycoStyle.borderColor,
                  },
                  "&.Mui-disabled": {
                    background: `${dycoStyle.background || dycoStyle.backgroundColor} !important`,
                    backgroundColor: `${dycoStyle.backgroundColor || dycoStyle.background} !important`,
                    borderColor: `${dycoStyle.borderColor} !important`,
                    color: `${dycoStyle.color} !important`,
                    opacity: 1,
                    cursor: "default",
                    pointerEvents: "none",
                    "& .MuiSvgIcon-root": {
                      fontSize: "15px",
                      color: `${dycoStyle.color} !important`,
                    },
                  },
                }}
              >
                {study.dycovueReportStatus === "requested" ? (
                  <Schedule sx={{ fontSize: "15px" }} />
                ) : study.dycovueReportStatus === "claimed" ? (
                  <AssignmentInd sx={{ fontSize: "15px" }} />
                ) : study.dycovueReportStatus === "completed" ? (
                  <CheckCircle sx={{ fontSize: "15px" }} />
                ) : (
                  <LocalHospital sx={{ fontSize: "15px" }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Send Email">
            <ModernIconButton
              variant="email"
              size="small"
              onClick={() => onSendEmail(study)}
            >
              <Send fontSize="small" />
            </ModernIconButton>
          </Tooltip>

          <Tooltip title="Patient History">
            <IconButton
              size="small"
              onClick={() => onViewHistory && onViewHistory(study)}
              sx={{
                padding: "3px",
                width: 26,
                height: 26,
                borderRadius: "6px",
                border: "1px solid #7C3AED",
                color: "#7C3AED",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: "rgba(124,58,237,0.1)", transform: "scale(1.08)" },
              }}
            >
              <HistoryIcon sx={{ fontSize: "15px" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
});

function ClinicianDashboard() {
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showRegisterPatient, setShowRegisterPatient] = useState(false);
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  useEffect(() => {
    console.log(
      "Dashboard.js state uploadDialogOpen changed to:",
      uploadDialogOpen,
    );
  }, [uploadDialogOpen]);

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    modality: "all",
  });

  const debouncedSearch = useDebounce(filters.search, 500);
  const [error, setError] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const { mode, toggleTheme: sharedToggleTheme } = useThemeMode();
  const isDark = mode === "dark";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const cardBorder = `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "#E2E8F0"}`;
  const cardBorderColor = isDark ? "rgba(255, 255, 255, 0.12)" : "#E2E8F0";
  const dividerColor = isDark ? "rgba(255, 255, 255, 0.12)" : "#F1F5F9";
  const headerBg = isDark ? "rgba(255, 255, 255, 0.04)" : "#F8FAFC";
  const textColorPrimary = isDark ? "#F1F5F9" : "#0F172A";
  const textColorSecondary = isDark ? "#CBD5E1" : "#64748B";
  const inputBg = isDark ? "#0F172A" : "#F8FAFC";
  const hoverBgSubtle = isDark ? "rgba(255, 255, 255, 0.08)" : "#F8FAFC";
  const hoverTealSubtle = isDark ? "rgba(13, 148, 136, 0.08)" : "#F0FDFA";
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [selfReportDialogOpen, setSelfReportDialogOpen] = useState(false);
  const [serverStats, setServerStats] = useState(null);
  const [requestDycovueDialogOpen, setRequestDycovueDialogOpen] =
    useState(false);
  const [reportViewerDialogOpen, setReportViewerDialogOpen] = useState(false);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [bulkSendDialogOpen, setBulkSendDialogOpen] = useState(false);
  const [selectedStudiesForBulk, setSelectedStudiesForBulk] = useState([]);
  const [editMetadataDialogOpen, setEditMetadataDialogOpen] = useState(false);
  const [patientDetailDialogOpen, setPatientDetailDialogOpen] = useState(false);
  const [labTestDialogOpen, setLabTestDialogOpen] = useState(false);
  const [labTestStudy, setLabTestStudy] = useState(null);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [serverStudies, setServerStudies] = useState([]);
  const [profilePictureTimestamp, setProfilePictureTimestamp] = useState(
    Date.now(),
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState("dashboard");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [todayWorkflowStudies, setTodayWorkflowStudies] = useState([]);
  const [todayWorkflowLoading, setTodayWorkflowLoading] = useState(false);
  const [uploadPrefillAccession, setUploadPrefillAccession] = useState("");
  // Patients view state
  const [patientsList, setPatientsList] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsPage, setPatientsPage] = useState(0);
  const [patientsRowsPerPage, setPatientsRowsPerPage] = useState(20);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientGender, setPatientGender] = useState("All");
  const [patientAgeRange, setPatientAgeRange] = useState("All");
  const [patientStatus, setPatientStatus] = useState("All");
  const [patientTab] = useState(0);
  const [, setPatientsTotalCount] = useState(0);
  const [selectedPatientForOrder, setSelectedPatientForOrder] = useState(null);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [editOrderReturnView, setEditOrderReturnView] = useState('ris_worklist');
  const [patientStartDate, setPatientStartDate] = useState("");
  const [patientEndDate, setPatientEndDate] = useState("");
  const [lastVisitStartDate, setLastVisitStartDate] = useState("");
  const [lastVisitEndDate, setLastVisitEndDate] = useState("");
  const [editPatientDialogOpen, setEditPatientDialogOpen] = useState(false);
  const [selectedPatientForEdit, setSelectedPatientForEdit] = useState(null);
  const [, setPatientEditForm] = useState({
    firstName: "",
    lastName: "",
    sex: "",
    birthDate: "",
    telephone: "",
    email: "",
    address: "",
  });
  const [viewPatientDialogOpen, setViewPatientDialogOpen] = useState(false);
  const [selectedPatientForView, setSelectedPatientForView] = useState(null);
  const [selectedPatientForProfile, setSelectedPatientForProfile] = useState(null);
  const [selectedLabReportId, setSelectedLabReportId] = useState(null);
  const [selectedLabPatientName, setSelectedLabPatientName] = useState("");
  const [labReportModalOpen, setLabReportModalOpen] = useState(false);

  const studiesRef = React.useRef(null);
  const notificationButtonRef = React.useRef(null);
  const profileButtonRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: sharedToggleTheme,
    }),
    [sharedToggleTheme],
  );

  const themeObj = React.useMemo(
    () => createTheme(getDesignTokens(mode)),
    [mode],
  );

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const fetchStudyStats = useCallback(async () => {
    try {
      const data = await getStudyStatistics();
      if (data) setServerStats(data);
    } catch (err) {
      console.error("Failed to fetch study statistics:", err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getUnreadNotifications();
      setUnreadCount(data.count || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  useEffect(() => {
    // Theme sync (settings fetch + 'userSettingsUpdated') now lives in ThemeModeContext.
    const handleProfilePictureUpdate = () => {
      setProfilePictureTimestamp(Date.now());
      if (refreshUser) {
        refreshUser();
      }
    };

    window.addEventListener(
      "profilePictureUpdated",
      handleProfilePictureUpdate,
    );

    return () => {
      window.removeEventListener(
        "profilePictureUpdated",
        handleProfilePictureUpdate,
      );
    };
  }, [refreshUser]);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event?.currentTarget || profileButtonRef.current);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchorEl(
      event?.currentTarget || notificationButtonRef.current,
    );
    await fetchNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationReceived = useCallback(
    async (data) => {
      await fetchNotifications();
    },
    [fetchNotifications],
  );

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await fetchNotifications();
      handleNotificationMenuClose();
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const fetchScanBoardPage = useCallback(
    async (pageIndex, perPage, searchTerm, modality) => {
      try {
        setLoading(true);
        setError("");
        const offset = (pageIndex || 0) * (perPage || 10);
        const params = {
          limit: perPage || 10,
          offset,
          sort: "-createdAt", // FIXED: Sort by newest first
        };

        // Check for facilityId in URL (Admin viewing facility images)
        const queryParams = new URLSearchParams(location.search);
        const facilityId = queryParams.get("facilityId");
        if (facilityId) {
          params.facility = facilityId;
        }

        // Enhanced search handling for patient names
        if (searchTerm) {
          const cleanedSearch = String(searchTerm).trim();

          // DICOM format: replace spaces with ^
          const dicomFormat = cleanedSearch.replace(/\s+/g, "^");
          // Normal format: replace ^ with space
          const normalFormat = cleanedSearch.replace(/\^/g, " ");

          // Send multiple search patterns to backend if it supports them
          params.search = cleanedSearch;
          params.searchPatterns = [cleanedSearch, dicomFormat, normalFormat];
        }
        if (modality && modality !== "all") params.modality = modality;

        // Use scan-board specific endpoint which aggregates by Image documents
        const res = await getScanBoardStudies(params);
        const allStudies = Array.isArray(res.studies) ? res.studies : [];
        // Exclude pending orders — studies with no DICOM images yet belong in RIS only
        setServerStudies(allStudies.filter(s => s.hasImages || (s.totalInstances || 0) > 0));
        if (typeof res.totalStudies === "number") {
          setServerStats((prev) => ({
            ...(prev || {}),
            totalStudies: res.totalStudies,
          }));
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching scan-board page:", err);
        setError("Failed to load studies: " + (err.message || "Unknown error"));
        setServerStudies([]);
        setLoading(false);
      }
    },
    [location.search],
  );

  const fetchTodayWorkflow = useCallback(async () => {
    try {
      setTodayWorkflowLoading(true);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      const [scanRes, patientRes, orderRes] = await Promise.all([
        getScanBoardStudies({ limit: 200, offset: 0 }),
        patientService.search({ limit: 200, systemSource: 'RIS' }),
        orderService.list({
          dateFrom: todayStart.toISOString(),
          dateTo: todayEnd.toISOString(),
          limit: 200,
        }),
      ]);

      const rawStudies = (Array.isArray(scanRes?.studies) ? scanRes.studies : [])
        .filter(s => s.hasImages || (s.totalInstances || 0) > 0);
      const todayStudies = rawStudies.filter(
        (s) => isTodayDate(s.studyDate) || isTodayDate(s.createdAt),
      );

      // Today's RIS orders — show as worklist rows for orders not yet in PACS
      const pacsAccessions = new Set(
        todayStudies.map((s) => String(s.accessionNumber || '').trim()).filter(Boolean)
      );
      const todayOrders = (orderRes?.data?.orders || orderRes?.data || []).filter(
        (o) => !pacsAccessions.has(String(o.accessionNumber || '').trim())
      );
      const orderRows = todayOrders.map((o) => {
        const rawName = o.patientName || '';
        // patientName stored as "Last^First" from backend
        const parts = rawName.includes('^') ? rawName.split('^') : rawName.split(' ');
        const displayName = rawName.includes('^')
          ? [parts[1], parts[0]].filter(Boolean).join(' ')
          : rawName;
        return {
          _id: `order-${o._id}`,
          orderDbId: o._id,
          isOrderOnly: true,
          patientId: o.patientMrn || (o.patient?.mrn ?? ''),
          patientMrn: o.patientMrn || (o.patient?.mrn ?? ''),
          patientName: displayName || `${o.patient?.firstName ?? ''} ${o.patient?.lastName ?? ''}`.trim(),
          patientSex: o.patientSex || o.patient?.sex,
          patientBirthDate: o.patientBirthDate || o.patient?.birthDate,
          studyDate: o.orderedAt || o.createdAt,
          accessionNumber: o.accessionNumber,
          modality: o.modality || '-',
          procedureDescription: o.procedureDescription || '-',
          orderStatus: o.orderStatus,
          totalInstances: 0,
          instanceCount: 0,
          hasImages: false,
          isPatientOnly: false,
        };
      });

      const patientPayload = patientRes?.data || patientRes;
      const patients = patientPayload?.patients || patientPayload?.items || [];
      const todayPatients = Array.isArray(patients)
        ? patients.filter((p) => isTodayDate(p.createdAt))
        : [];

      // Accessions/MRNs already covered by PACS studies or orders
      const orderMrns = new Set([
        ...todayStudies.map((s) => String(s.patientId || s.patientMrn || '').trim()),
        ...todayOrders.map((o) => String(o.patientMrn || '').trim()),
      ].filter(Boolean));

      const patientOnlyRows = todayPatients
        .filter((p) => p.mrn && !orderMrns.has(p.mrn))
        .map((p) => ({
          _id: `patient-${p._id}`,
          patientDbId: p._id,          // real MongoDB _id for order creation
          isPatientOnly: true,
          patientId: p.mrn,
          patientMrn: p.mrn,
          patientName: [p.firstName, p.lastName].filter(Boolean).join(" "),
          patientSex: p.sex,
          patientBirthDate: p.birthDate,
          studyDate: p.createdAt,
          accessionNumber: "-",
          modality: "-",
          totalInstances: 0,
          instanceCount: 0,
          hasImages: false,
        }));

      setTodayWorkflowStudies(
        [...todayStudies, ...orderRows, ...patientOnlyRows].sort((a, b) => {
          const ta = new Date(a.createdAt || a.orderedAt || a.studyDate || 0).getTime();
          const tb = new Date(b.createdAt || b.orderedAt || b.studyDate || 0).getTime();
          return tb - ta; // newest created/ordered first
        })
      );
    } catch (err) {
      console.error("Failed to fetch today's workflow:", err);
      setTodayWorkflowStudies([]);
    } finally {
      setTodayWorkflowLoading(false);
    }
  }, []);

  // NOTE: Search is now handled entirely by backend.
  // Backend searches across ALL studies before pagination.
  // Patient name search supports both formats: "Last First" and "Last^First"
  // Also searches patient ID and study description.

  const loadStudies = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await fetchScanBoardPage(
        page,
        rowsPerPage,
        debouncedSearch,
        filters.modality,
      );
      await fetchStudyStats();
    } catch (err) {
      console.error("Error loading studies metadata:", err);
    }
  }, [
    fetchScanBoardPage,
    page,
    rowsPerPage,
    debouncedSearch,
    filters.modality,
    fetchStudyStats,
  ]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchScanBoardPage(0, rowsPerPage, "", "");
        await fetchStudyStats();
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial dashboard load on mount / URL change
  }, [location.search]);

  useEffect(() => {
    setPage(0);
    fetchScanBoardPage(0, rowsPerPage, debouncedSearch, filters.modality);
  }, [debouncedSearch, filters.modality, fetchScanBoardPage, rowsPerPage]);

  useEffect(() => {
    if (currentView === "dashboard") {
      fetchTodayWorkflow();
    }
  }, [currentView, fetchTodayWorkflow]);

  // Patients fetcher: load when the patients view is active
  const fetchPatients = useCallback(
    async (pageIndex = 0, perPage = 10, q = "", gender = "All") => {
      try {
        setPatientsLoading(true);
        const params = { limit: perPage, page: pageIndex + 1, systemSource: 'RIS' };
        if (q) params.q = q;
        if (gender && gender !== "All") {
          params.sex =
            gender === "Male" ? "M" : gender === "Female" ? "F" : undefined;
        }
        const res = await patientService.search(params);
        const data = res?.data || res;
        const items = data?.patients || data?.items || data || [];
        setPatientsList(Array.isArray(items) ? items : []);
        setPatientsTotalCount(data?.total || items.length || 0);
      } catch (err) {
        console.error("Failed to fetch patients:", err);
        setPatientsList([]);
        setPatientsTotalCount(0);
      } finally {
        setPatientsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (currentView === "patients") {
      fetchPatients(
        patientsPage,
        patientsRowsPerPage,
        patientSearch,
        patientGender,
      );
    }
  }, [
    currentView,
    patientsPage,
    patientsRowsPerPage,
    patientSearch,
    patientGender,
    fetchPatients,
  ]);

  const handleOpenEditPatient = (p) => {
    setSelectedPatientForEdit(p);
    setPatientEditForm({
      firstName: p.firstName || "",
      lastName: p.lastName || "",
      sex: p.sex || p.patientSex || "M",
      birthDate: p.birthDate
        ? new Date(new Date(p.birthDate).getTime() + 12 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
        : "",
      telephone: p.telephone || p.phone || "",
      email: p.email || "",
      address: p.address || "",
    });
    setEditPatientDialogOpen(true);
  };

  const handleOpenViewPatient = (p) => {
    setSelectedPatientForView(p);
    setViewPatientDialogOpen(true);
  };

  useEffect(() => {
    if (user?._id) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 5000);
      return () => clearInterval(interval);
    }
  }, [user?._id, fetchNotifications]);

  const handleSyncComplete = useCallback(
    async (data) => {
      try {
        if (data && typeof data === "object" && "addedImages" in data) {
          const {
            addedImages = 0,
            updatedImages = 0,
            deletedImages = 0,
            studyCreated = 0,
            studyUpdated = 0,
          } = data;
          if (
            addedImages +
              updatedImages +
              deletedImages +
              studyCreated +
              studyUpdated ===
            0
          ) {
            return;
          }
        }

        await fetchScanBoardPage(
          page,
          rowsPerPage,
          debouncedSearch,
          filters.modality,
        );
        await fetchStudyStats();
        await fetchTodayWorkflow();
      } catch (err) {
        console.error("Error handling sync complete:", err);
      }
    },
    [
      fetchScanBoardPage,
      page,
      rowsPerPage,
      debouncedSearch,
      filters.modality,
      fetchStudyStats,
      fetchTodayWorkflow,
    ],
  );

  // Periodic background live refresh (keeps study status, orders, and badges updated without page reloads)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
      fetchStudyStats();
      fetchTodayWorkflow();
    }, 15000);
    return () => clearInterval(interval);
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    filters.modality,
    fetchScanBoardPage,
    fetchStudyStats,
    fetchTodayWorkflow,
  ]);

  const [syncing, setSyncing] = useState(false);
  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await syncOrthanc();
      setUploadStatus(`Sync successful: ${res.message}`);
      await fetchScanBoardPage(
        0,
        rowsPerPage,
        debouncedSearch,
        filters.modality,
      );
      await fetchStudyStats();
    } catch (err) {
      console.error("Sync error:", err);
      setError(
        "Failed to sync with Orthanc: " + (err.message || "Unknown error"),
      );
    } finally {
      setSyncing(false);
    }
  };

  const totalStudyCount = serverStats?.totalStudies ?? 0;

  const paginatedStudies = React.useMemo(() => {
    const studies = serverStudies.map((study) => ({
      ...study,
      patient: {
        patientId: study.patientId,
        patientName: study.patientName,
        patientSex: study.patientSex,
        patientBirthDate: study.patientBirthDate,
      },
    }));

    // Return studies from backend (already filtered and paginated)
    return studies;
  }, [serverStudies]);

  // Helper: Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return "";
    // DICOM birth dates have no time/timezone component — extract via UTC
    // (with a 12h buffer to absorb storage skew) so this never rolls back a
    // day depending on the viewer's timezone, same as formatDicomDate.
    const raw = birthDate instanceof Date ? birthDate : new Date(birthDate);
    if (isNaN(raw.getTime())) return "";
    const birth = new Date(raw.getTime() + 12 * 60 * 60 * 1000);
    const birthY = birth.getUTCFullYear();
    const birthM = birth.getUTCMonth();
    const birthD = birth.getUTCDate();
    const now = new Date();
    let age = now.getFullYear() - birthY;
    const m = now.getMonth() - birthM;
    if (m < 0 || (m === 0 && now.getDate() < birthD)) age--;
    return `${age} Y`;
  };

  // Helper: Get status info for a study
  const getStudyStatusInfo = (study) => {
    const label = resolveWorkflowStatusLabel(study);
    return {
      label,
      color: WORKFLOW_STATUS_COLORS[label] || "#6B7280",
    };
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // FIXED: Use debouncedSearch instead of filters.search
    fetchScanBoardPage(newPage, rowsPerPage, debouncedSearch, filters.modality);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    // FIXED: Use debouncedSearch instead of filters.search
    fetchScanBoardPage(0, newRows, debouncedSearch, filters.modality);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const handleBackToAdmin = () => {
    navigate("/admin/facilities");
  };

  const handleMenuClick = (item) => {
    setCurrentView(item.key);
    if (item.key === "orders") {
      setSelectedPatientForOrder(null);
    }
    if (item.action) {
      item.action();
    }
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Roles that cannot register new patients (read-only access to patient list)
  const canRegisterPatients = !['referring_physician', 'nurse', 'medical_records_officer'].includes(user?.role);
  // Roles that cannot create imaging orders
  const canCreateOrders = !['nurse', 'medical_records_officer'].includes(user?.role);
  const canManageRooms = user?.role === 'super_clinician';
  const canManageUsers = user?.role === 'super_clinician';

  const menuItems = [
    { key: "dashboard", text: "Dashboard", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 1 18 0c0 2.5-1.5 5-4 7l-5 3-5-3c-2.5-2-4-4.5-4-7z" />
        <path d="M12 7v5l3 2" strokeWidth="2.5" />
      </svg>
    ) },
    { key: "patients", text: "Patients", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ) },
    ...(canCreateOrders ? [{ key: "orders", text: "Create Order", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        <path d="M12 11v6" strokeWidth="2.5" />
        <path d="M9 14h6" strokeWidth="2.5" />
      </svg>
    ) }] : []),
    { key: "ris_worklist", text: "RIS Worklist", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
        <line x1="9" y1="12" x2="15" y2="12" strokeWidth="2.5" />
        <line x1="9" y1="16" x2="13" y2="16" strokeWidth="2.5" />
      </svg>
    ) },
    { key: "scheduling", text: "Scheduling", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="17" rx="2" ry="2" />
        <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2.5" />
        <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2.5" />
        <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2.5" />
      </svg>
    ) },
    ...(canManageRooms ? [{ key: "rooms", text: "Rooms & Equipment", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" strokeWidth="2.5" />
        <line x1="10" y1="14" x2="14" y2="14" strokeWidth="2.5" />
      </svg>
    ) }] : []),
    ...(canManageUsers ? [{ key: "users", text: "Manage Users", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ) }] : []),
    { key: "settings", text: "Settings", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ) },
    { key: "help", text: "Help & Support", icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="3" />
      </svg>
    ) },
  ];

  const handleUpload = async (formData) => {
    setUploading(true);
    setError("");
    setUploadStatus("");

    try {
      const response = await uploadImage(formData);

      if (response && response.zipFile && response.images) {
        setUploadStatus(
          `ZIP processed successfully! ${response.totalFiles} DICOM files uploaded across ${response.studies} studies.`,
        );
      } else if (response && response.conversionPerformed) {
        setUploadStatus("Image converted to DICOM format successfully!");
      } else if (response && response.image) {
        setUploadStatus("File uploaded successfully!");
      } else {
        setUploadStatus(
          "Upload completed, but response format was unexpected.",
        );
      }

      if (response && response.orthancStatus === "success") {
        setUploadStatus(
          (prev) => prev + " Files uploaded to Orthanc successfully!",
        );
      } else if (response && response.orthancError) {
        setUploadStatus((prev) => prev + ` Warning: ${response.orthancError}`);
      }

      setUploadDialogOpen(false);

      const isZipFile = response && response.zipFile;
      setTimeout(
        () => {
          fetchScanBoardPage(
            page,
            rowsPerPage,
            debouncedSearch,
            filters.modality,
          );
          fetchStudyStats();
        },
        isZipFile ? 2000 : 1000,
      );

      return response;
    } catch (err) {
      console.error("Upload error:", err);

      let errorMessage = "Upload failed";

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (errorMessage.includes("zipFile")) {
        errorMessage =
          "ZIP file processing failed. Please check the file format and try again.";
      } else if (errorMessage.includes("duplicate key")) {
        errorMessage =
          "Some images may already exist in the database. Please check for duplicates.";
      }

      setError(errorMessage);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Open OHIF Viewer in new tab with better error handling
  const openOHIFViewer = (study) => {
    const studyInstanceUID = study.studyInstanceUID;

    const uid =
      studyInstanceUID ||
      study.studyInstanceUID ||
      study.studyUID ||
      study.orthancStudyId;
    if (!uid) {
      alert(
        `Unable to open viewer: Study UID is missing. The study may still be processing.`,
      );
      return false;
    }

    try {
      const ohifBaseUrl =
        process.env.REACT_APP_OHIF_URL ||
        window.env?.REACT_APP_OHIF_URL ||
        "http://localhost:8042/ohif";
      const viewerUrl = generateOHIFUrl(uid, ohifBaseUrl);

      console.log("Opening OHIF viewer:", viewerUrl);

      const newWindow = window.open(
        viewerUrl,
        "_blank",
        "noopener,noreferrer,width=1400,height=900,scrollbars=yes,resizable=yes",
      );
      if (newWindow) {
        newWindow.opener = null;
        try {
          newWindow.focus();
        } catch (e) {}
        return true;
      } else {
        // Popup blocked: fallback to link click
        const link = document.createElement("a");
        link.href = viewerUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          try {
            document.body.removeChild(link);
          } catch (e) {}
        }, 100);
        return true;
      }
    } catch (error) {
      console.error("Error opening OHIF viewer:", error);
      const ohifBaseUrl =
        process.env.REACT_APP_OHIF_URL || "http://localhost:8042/ohif";
      const uidFallback =
        studyInstanceUID ||
        study.studyInstanceUID ||
        study.studyUID ||
        study.orthancStudyId ||
        "";
      const viewerUrl = generateOHIFUrl(uidFallback, ohifBaseUrl);
      alert(
        `Could not open viewer automatically. Please copy and paste this URL in a new tab:\n\n${viewerUrl}`,
      );
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(viewerUrl)
          .catch((e) => console.error("Clipboard error:", e));
      }
      return false;
    }
  };

  const handleCreateSelfReport = (study) => {
    setSelectedStudy(study);

    // If report is completed/approved, show ReportViewer with addendum button
    if (
      study.selfReportStatus === "completed" ||
      study.selfReportStatus === "approved"
    ) {
      setReportViewerDialogOpen(true); // ✅ Show ReportViewer instead
    } else {
      setSelfReportDialogOpen(true); // ✅ Show create/edit form
    }
  };
  const handleRequestDycovue = (study) => {
    setSelectedStudy(study);
    setRequestDycovueDialogOpen(true);
  };

  const [historyPanel, setHistoryPanel] = useState({ open: false, study: null });
  const handleOpenHistory = (study) => setHistoryPanel({ open: true, study });
  const handleCloseHistory = () => setHistoryPanel({ open: false, study: null });

  const handleViewReports = (study) => {
    setSelectedStudy(study);
    setReportViewerDialogOpen(true);
  };

  const handleViewImages = (study) => {
    const success = openOHIFViewer(study);
    if (!success) console.warn("Failed to open OHIF viewer for study:", study);
  };

  const handleWorkflowUpload = (study) => {
    setUploadPrefillAccession(study.accessionNumber || "");
    setUploadDialogOpen(true);
  };

  const handleSendEmail = (study) => {
    setSelectedStudy(study);
    setSendEmailDialogOpen(true);
  };

  const handleToggleSelectStudy = (studyData) => {
    setSelectedStudiesForBulk((prev) => {
      const exists = prev.some((s) => s._id === studyData._id);
      return exists ? prev.filter((s) => s._id !== studyData._id) : [...prev, studyData];
    });
  };

  const handleOpenBulkSend = () => {
    if (selectedStudiesForBulk.length > 0) setBulkSendDialogOpen(true);
  };

  const handleClearBulkSelection = () => setSelectedStudiesForBulk([]);

  const handleEditMetadata = (study) => {
    setSelectedStudy(study);
    setEditMetadataDialogOpen(true);
  };

  const handleViewDetails = (study) => {
    setSelectedStudy(study);
    setPatientDetailDialogOpen(true);
  };

  // For isPatientOnly rows in Today's Worklist — shows the patient details dialog
  const handleViewPatientDetails = (study) => {
    setSelectedStudy(study);
    setPatientDetailDialogOpen(true);
  };

  // For isPatientOnly rows in Today's Worklist — opens Create Order page with patient pre-filled
  const handleCreateOrderForPatient = (study) => {
    // Build a patient object with the real MongoDB _id so CreateOrder sends a valid ObjectId
    const patientObj = {
      _id: study.patientDbId || study._id,  // patientDbId is the real MongoDB _id
      mrn: study.patientMrn || study.patientId,
      firstName: (study.patientName || '').split(' ')[0] || '',
      lastName: (study.patientName || '').split(' ').slice(1).join(' ') || '',
      patientName: study.patientName,
      patientId: study.patientMrn || study.patientId,
      // Include DOB and sex so they display in the Create Order patient card
      birthDate: study.patientBirthDate,
      sex: study.patientSex,
    };
    setSelectedPatientForOrder(patientObj);
    setCurrentView('orders');
  };

  const filteredPatientsByTab = React.useMemo(() => {
    if (!Array.isArray(patientsList)) return [];

    return patientsList.filter((p) => {
      if (patientStatus !== "All") {
        const pStatus = p.status || "Active";
        if (pStatus.toLowerCase() !== patientStatus.toLowerCase()) return false;
      }

      if (patientAgeRange !== "All") {
        const ageStr = p.birthDate ? calculateAge(p.birthDate) : "";
        const ageNum = parseInt(ageStr, 10);
        if (isNaN(ageNum)) return false;
        if (patientAgeRange === "0-18" && ageNum > 18) return false;
        if (patientAgeRange === "19-40" && (ageNum < 19 || ageNum > 40))
          return false;
        if (patientAgeRange === "41-65" && (ageNum < 41 || ageNum > 65))
          return false;
        if (patientAgeRange === "66+" && ageNum < 66) return false;
      }

      if (patientStartDate) {
        if (!p.createdAt) return false;
        const regTime = new Date(p.createdAt).getTime();
        const startTime = new Date(patientStartDate).getTime();
        if (regTime < startTime) return false;
      }

      if (patientEndDate) {
        if (!p.createdAt) return false;
        const regTime = new Date(p.createdAt).getTime();
        const endTime = new Date(patientEndDate).getTime() + (24 * 60 * 60 * 1000 - 1);
        if (regTime > endTime) return false;
      }

      // Last Visit filter — based on p.lastVisit (set by backend when order is created)
      if (lastVisitStartDate) {
        if (!p.lastVisit) return false;
        const lvTime = new Date(p.lastVisit).getTime();
        const startTime = new Date(lastVisitStartDate).getTime();
        if (lvTime < startTime) return false;
      }

      if (lastVisitEndDate) {
        if (!p.lastVisit) return false;
        const lvTime = new Date(p.lastVisit).getTime();
        const endTime = new Date(lastVisitEndDate).getTime() + (24 * 60 * 60 * 1000 - 1);
        if (lvTime > endTime) return false;
      }

      if (patientTab === 0) return true;

      const regDate = p.createdAt ? new Date(p.createdAt) : new Date();
      const today = new Date();

      if (patientTab === 1) {
        return regDate.toDateString() === today.toDateString();
      }
      if (patientTab === 2) {
        const diffTime = Math.abs(today - regDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
      }
      if (patientTab === 3) {
        return (
          regDate.getMonth() === today.getMonth() &&
          regDate.getFullYear() === today.getFullYear()
        );
      }
      return true;
    });
  }, [patientsList, patientTab, patientStatus, patientAgeRange, patientStartDate, patientEndDate, lastVisitStartDate, lastVisitEndDate]);

  if (loading && !serverStudies.length)
    return (
      <Box sx={{ width: "100%", position: "fixed", top: 0, left: 0 }}>
        <LinearProgress
          sx={{
            height: "3px",
            backgroundColor: "transparent",
            "& .MuiLinearProgress-bar": {
              background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
            },
          }}
        />
      </Box>
    );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={themeObj}>
        <CssBaseline />
        {user?._id && (
          <NotificationListener
            userId={user._id}
            onNotificationReceived={handleNotificationReceived}
            onSyncComplete={handleSyncComplete}
          />
        )}
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            bgcolor: isDark ? "#0A0A0A" : "#F8FAFC",
            position: "relative",
            zIndex: 1,
          }}
        >
          <AppBar
            position="fixed"
            sx={{
              width: {
                md: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)`,
              },
              ml: {
                md: `${drawerOpen ? drawerWidth : collapsedDrawerWidth}px`,
              },
              zIndex: (theme) => theme.zIndex.drawer - 1,
              background: cardBg,
              boxShadow: "none",
              borderBottom: cardBorder,
              transition: themeObj.transitions.create(["width", "margin"], {
                easing: themeObj.transitions.easing.sharp,
                duration: themeObj.transitions.duration.enteringScreen,
              }),
            }}
          >
            <Toolbar sx={{ justifyContent: "space-between", gap: 2, px: { xs: 1.5, sm: 2.5 }, minHeight: { xs: '48px !important', sm: '52px !important' }, height: { xs: '48px', sm: '52px' } }}>
              {/* Left Section: Dynamic title based on view matching mockup */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Tooltip title={drawerOpen ? "Collapse sidebar" : "Expand sidebar"} arrow>
                  <IconButton
                    onClick={handleDrawerToggle}
                    edge="start"
                    sx={{
                      color: textColorPrimary,
                      p: 0.75,
                      borderRadius: "6px",
                      border: cardBorder,
                      width: 32,
                      height: 32,
                      mr: 0.5,
                      "&:hover": {
                        backgroundColor: hoverBgSubtle,
                        color: "#0D9488",
                        borderColor: "#0D9488",
                      },
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                {/* Checklist icon container */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    color: "#0D9488",
                    bgcolor: "rgba(13, 148, 136, 0.1)",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    <path d="M9 12h6" strokeWidth="2.5" />
                    <path d="M9 16h6" strokeWidth="2.5" />
                  </svg>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: '"Satoshi", sans-serif' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: "#0D9488",
                      fontSize: "1.1rem",
                      fontFamily: '"Satoshi", sans-serif',
                    }}
                  >
                    RIS
                  </Typography>

                  {currentView !== "dashboard" && (
                    <>
                      <Typography sx={{ color: "#94A3B8", fontWeight: 700, mx: 0.25, fontSize: "0.9rem", fontFamily: '"Satoshi", sans-serif' }}>&gt;</Typography>
                      {(currentView === "patients" || currentView === "patientProfile") && (
                        <>
                          <Typography
                            onClick={() => setCurrentView("patients")}
                            variant="h6"
                            sx={{
                              fontWeight: currentView === "patientProfile" ? 700 : 800,
                              color: currentView === "patientProfile" ? textColorSecondary : textColorPrimary,
                              fontSize: "1.1rem",
                              cursor: currentView === "patientProfile" ? "pointer" : "default",
                              fontFamily: '"Satoshi", sans-serif',
                              "&:hover": { color: currentView === "patientProfile" ? "#0D9488" : "inherit" }
                            }}
                          >
                            Patients
                          </Typography>
                          {currentView === "patientProfile" && (
                            <>
                              <Typography sx={{ color: "#94A3B8", fontWeight: 700, mx: 0.25, fontSize: "0.9rem", fontFamily: '"Satoshi", sans-serif' }}>&gt;</Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: textColorPrimary,
                                  fontSize: "1.1rem",
                                  fontFamily: '"Satoshi", sans-serif',
                                }}
                              >
                                Patient Profile
                              </Typography>
                            </>
                          )}
                        </>
                      )}
                      {currentView === "registerPatient" && (
                        <>
                          <Typography
                            onClick={() => setCurrentView("patients")}
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: textColorSecondary,
                              fontSize: "1.1rem",
                              cursor: "pointer",
                              fontFamily: '"Satoshi", sans-serif',
                              "&:hover": { color: "#0D9488" }
                            }}
                          >
                            Patients
                          </Typography>
                          <Typography sx={{ color: "#94A3B8", fontWeight: 700, mx: 0.25, fontSize: "0.9rem", fontFamily: '"Satoshi", sans-serif' }}>&gt;</Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: textColorPrimary,
                              fontSize: "1.1rem",
                              fontFamily: '"Satoshi", sans-serif',
                            }}
                          >
                            New Patient
                          </Typography>
                        </>
                      )}
                      {currentView !== "patients" && currentView !== "registerPatient" && currentView !== "patientProfile" && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            color: textColorPrimary,
                            fontSize: "1.1rem",
                            fontFamily: '"Satoshi", sans-serif',
                          }}
                        >
                          {currentView === "orders" ? "Create Order" :
                           currentView === "editOrder" ? "Edit Order" :
                           currentView === "ris_worklist" ? "RIS Worklist" :
                           currentView === "scheduling" ? "Scheduling" :
                           currentView === "rooms" ? "Rooms & Equipment" :
                           currentView === "users" ? "Manage Users" :
                           currentView === "imaging" ? "Imaging" :
                           currentView === "reports" ? "Reports" :
                           currentView === "settings" ? "Settings" :
                           currentView === "profile" ? "Profile" : currentView}
                        </Typography>
                      )}
                    </>
                  )}
                  {currentView === "dashboard" && (
                    <>
                      <Typography sx={{ color: "#94A3B8", fontWeight: 700, mx: 0.25, fontSize: "0.9rem", fontFamily: '"Satoshi", sans-serif' }}>&gt;</Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          color: textColorPrimary,
                          fontSize: "1.1rem",
                          fontFamily: '"Satoshi", sans-serif',
                        }}
                      >
                        Dashboard
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Switch to PACS button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/radiologist')}
                  startIcon={
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  }
                  sx={{
                    ml: 3,
                    px: 1.5,
                    py: 0.5,
                    height: '30px',
                    borderColor: '#E2E8F0',
                    color: '#2563EB',
                    backgroundColor: '#EFF6FF',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    '&:hover': {
                      borderColor: '#3B82F6',
                      backgroundColor: '#DBEAFE',
                    }
                  }}
                >
                  Switch to PACS
                </Button>

                {/* Switch to LIS button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/laboratory')}
                  startIcon={
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 3v7l-2 4v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4l-2-4V3" />
                      <line x1="9" y1="3" x2="15" y2="3" />
                      <path d="M8 14h8" />
                    </svg>
                  }
                  sx={{
                    ml: 1,
                    px: 1.5,
                    py: 0.5,
                    height: '30px',
                    borderColor: '#99F6E4',
                    color: '#0D9488',
                    backgroundColor: '#F0FDFA',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    '&:hover': {
                      borderColor: '#0D9488',
                      backgroundColor: '#CCFBF1',
                    }
                  }}
                >
                  Switch to LIS
                </Button>
              </Box>

              {/* Right Section: Notification bell with green badge, user profile avatar stack */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Theme Toggle (subtle micro-animation) */}
                <IconButton
                  onClick={colorMode.toggleColorMode}
                  sx={{
                    color: textColorSecondary,
                    mr: 0.5,
                    "&:hover": {
                      backgroundColor: hoverBgSubtle,
                    },
                  }}
                >
                  {mode === "dark" ? (
                    <Brightness7 sx={{ fontSize: 20 }} />
                  ) : (
                    <Brightness4 sx={{ fontSize: 20 }} />
                  )}
                </IconButton>

                {/* Bell notification button with green badge count of 3 */}
                <IconButton
                  ref={notificationButtonRef}
                  onClick={handleNotificationMenuOpen}
                  sx={{
                    color: textColorSecondary,
                    mr: 0.5,
                    "&:hover": {
                      backgroundColor: hoverBgSubtle,
                    },
                  }}
                >
                    <NotificationsIcon sx={{ fontSize: 22 }} />
                </IconButton>

                {/* User Profile dropdown wrapper */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    ml: 1.5,
                    pl: 1.5,
                    borderLeft: cardBorder,
                    cursor: "pointer",
                  }}
                  onClick={handleProfileMenuOpen}
                >
                  {/* Initials Avatar container matching mockup */}
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "#E2E8F0",
                      color: "#475569",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      border: "1px solid #CBD5E1",
                    }}
                  >
                    {user?.firstName && user?.lastName
                      ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                      : "JS"}
                  </Avatar>

                  {/* Name and Department stack */}
                  <Box
                    sx={{
                      textAlign: "left",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: textColorPrimary,
                        fontSize: "0.85rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {user?.firstName
                        ? `Dr. ${user.firstName} ${user.lastName}`
                        : "Dr. John Smith"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: textColorSecondary,
                        display: "block",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                      }}
                    >
                      {user?.department || user?.institutionRole || (user?.role ? ({
                        clinician: 'Clinician',
                        super_clinician: 'Super Clinician',
                        radiologist: 'Radiologist',
                        in_house_radiologist: 'In-House Radiologist',
                        facility_admin: 'Facility Admin',
                        admin: 'Administrator',
                        technologist: 'Technologist',
                        radiographer: 'Radiographer',
                        sonographer: 'Sonographer',
                        referring_physician: 'Referring Physician',
                        nurse: 'Nurse',
                        medical_records_officer: 'Medical Records',
                        lab_admin: 'Lab Admin',
                        lab_scientist: 'Lab Scientist',
                        senior_lab_scientist: 'Sr. Lab Scientist',
                        pathologist: 'Pathologist',
                        histotechnologist: 'Histotechnologist',
                      }[user.role] || user.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : "Clinical Services")}
                    </Typography>
                  </Box>

                  {/* Down Chevron Arrow */}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={textColorSecondary} strokeWidth="2.5">
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              </Box>


              {/* Notification Menu */}
              <Menu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    width: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxHeight: 520,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    borderRadius: "14px",
                    boxShadow: themeObj.palette.mode === 'dark'
                      ? '0 12px 36px rgba(0,0,0,0.6)'
                      : '0 12px 36px rgba(0,0,0,0.12)',
                    border: `1px solid ${themeObj.palette.divider}`,
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${themeObj.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: "1rem", fontWeight: 600 }}
                    >
                      Notifications
                    </Typography>
                    {unreadCount > 0 && (
                      <Button
                        size="small"
                        onClick={handleMarkAllAsRead}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Mark all read
                      </Button>
                    )}
                  </Box>
                </Box>
                {notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <MenuItem
                      key={notification._id || index}
                      onClick={() => {
                        if (notification.type === 'lab_report_ready') {
                          setSelectedLabReportId(notification.labReportId || notification.labReportOrderId);
                          setSelectedLabPatientName(notification.patientName);
                          setLabReportModalOpen(true);
                        } else if (notification.studyUID) {
                          navigate(`/viewer/${notification.studyUID}?reportId=${notification.reportId}`);
                        } else {
                          navigate(`/dashboard?reportId=${notification.reportId}`);
                        }
                        handleNotificationMenuClose();
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        whiteSpace: 'normal',
                        alignItems: 'flex-start',
                        borderBottom:
                          index < notifications.length - 1
                            ? `1px solid ${themeObj.palette.divider}`
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            themeObj.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%", minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            mb: 0.5,
                            color: notification.type === 'report_flagged' ? '#EF4444' : 'inherit',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            lineHeight: 1.45,
                          }}
                        >
                          {notification.type === 'lab_report_ready' ? '🧪 ' : (notification.type === 'report_flagged' ? '🚩 ' : (notification.priority === "stat" ? "🚨 " : "📋 "))}
                          {(notification.message || (notification.type === 'lab_report_ready' ? 'Lab Report Ready' : (notification.type === 'report_flagged' ? 'Report Concerns Flagged' : 'Report Ready'))).replace(/\^/g, ' ')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{
                            display: 'block',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                          }}
                        >
                          {formatPatientName(notification.patientName)} • {notification.type === 'lab_report_ready' ? 'Laboratory Department' : (notification.modality || 'Imaging')}
                        </Typography>
                        {notification.timestamp && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ display: "block", mt: 0.5, fontSize: '0.72rem' }}
                          >
                            {new Date(notification.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="textSecondary">
                      No new notifications
                    </Typography>
                  </Box>
                )}
              </Menu>

              {/* Profile Menu */}
              <Menu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    borderRadius: "12px",
                    boxShadow:
                      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <MenuItem
                  onClick={handleProfileMenuClose}
                  sx={{ py: 1.5, px: 2 }}
                >
                  {user?.profilePicture ? (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        borderRadius: "50%",
                        backgroundImage: `url(${user.profilePicture}?t=${profilePictureTimestamp})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        border: "1px solid #E2E8F0",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        bgcolor: themeObj.palette.primary.main,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      {user?.firstName?.charAt(0)}
                      {user?.lastName?.charAt(0)}
                    </Avatar>
                  )}
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="semibold"
                      sx={{ color: "text.primary" }}
                    >
                      {user?.firstName} {user?.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setCurrentView("profile");
                    handleProfileMenuClose();
                  }}
                >
                  <Person fontSize="small" sx={{ mr: 2, color: "#64748B" }} />
                  {t("dashboard.profile")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setChangePasswordOpen(true);
                    handleProfileMenuClose();
                  }}
                >
                  <KeyIcon fontSize="small" sx={{ mr: 2, color: "#64748B" }} />
                  {t("dashboard.changePassword")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setCurrentView("settings");
                    handleProfileMenuClose();
                  }}
                >
                  <Settings fontSize="small" sx={{ mr: 2, color: "#64748B" }} />
                  Settings
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate("/portal");
                    handleProfileMenuClose();
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "16px" }}>
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  Switch Portal
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon
                    fontSize="small"
                    sx={{ mr: 2, color: "#EF4444" }}
                  />
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                  >
                    Logout
                  </Typography>
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          {/* Sidebar Drawer */}
          <StyledDrawer
            variant={isMobile ? "temporary" : "permanent"}
            open={drawerOpen}
            onClose={handleDrawerToggle}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                px: drawerOpen ? 2 : 1,
                py: 3,
                position: "relative",
                transition: "padding 0.2s ease",
              }}
            >
              {isMobile && (
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    zIndex: 10,
                    opacity: 0.5,
                    "&:hover": {
                      opacity: 0.75,
                    },
                    display: { md: "none" },
                  }}
                >
                  <Close />
                </IconButton>
              )}

              {/* Logo section inside Sidebar with Collapse/Expand toggle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: drawerOpen ? "space-between" : "center",
                  px: drawerOpen ? 1 : 0.5,
                  py: 1,
                  mb: 2.5,
                  flex: "none !important",
                  width: "100%",
                }}
              >
                {drawerOpen ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
                      <FullLogo />
                    </Box>
                    <Tooltip title="Collapse sidebar" placement="right" arrow>
                      <IconButton
                        size="small"
                        onClick={handleDrawerToggle}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "6px",
                          border: cardBorder,
                          color: textColorSecondary,
                          "&:hover": {
                            color: "#0D9488",
                            backgroundColor: hoverTealSubtle,
                            borderColor: "#0D9488",
                          },
                        }}
                      >
                        <ChevronLeft sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, py: 0.5, width: "100%" }}>
                    <Tooltip title="Expand sidebar" placement="right" arrow>
                      <IconButton
                        size="small"
                        onClick={handleDrawerToggle}
                        sx={{
                          p: 0.5,
                          borderRadius: "8px",
                          border: cardBorder,
                          bgcolor: cardBg,
                          "&:hover": {
                            backgroundColor: hoverTealSubtle,
                            borderColor: "#0D9488",
                          },
                        }}
                      >
                        <IconLogo size={26} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  overflowY: "auto",
                  mt: 1,
                  minHeight: 0,
                  flex: "1 1 auto !important",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                <List sx={{ py: 0, pb: 4, flex: "none !important" }}>
                  {menuItems.map((item) => {
                    const isActive = currentView === item.key;
                    const inactiveColor = mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B";
                    const iconColor = isActive ? "#0D9488" : inactiveColor;

                    return (
                      <Tooltip
                        key={item.text}
                        title={!drawerOpen ? item.text : ""}
                        placement="right"
                        arrow
                      >
                        <StyledListItemButton
                          selected={isActive}
                          onClick={() => handleMenuClick(item)}
                          isDark={false}
                          sx={{
                            flex: "none !important",
                            width: drawerOpen ? "auto" : 44,
                            minWidth: drawerOpen ? "auto" : 44,
                            height: 44,
                            m: drawerOpen ? "4px 12px 8px 12px !important" : "4px auto 8px auto !important",
                            px: drawerOpen ? 2 : 0,
                            justifyContent: drawerOpen
                              ? "flex-start"
                              : "center",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: drawerOpen ? 36 : "auto",
                              color: iconColor,
                              justifyContent: "center",
                              flex: "none !important",
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {drawerOpen && (
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 700 : 500,
                                fontSize: "0.875rem",
                                color: isActive ? (mode === "dark" ? "#2DD4BF" : "#00665C") : inactiveColor,
                                textAlign: "left",
                              }}
                              sx={{ flex: "none !important" }}
                            />
                          )}
                        </StyledListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Box>

              {/* Bottom section of Sidebar */}
              <Box
                sx={{
                  mt: "auto",
                  pt: 2,
                  flexShrink: 0,
                  flex: "none !important",
                }}
              >

                <Tooltip title={!drawerOpen ? "Log Out" : ""} placement="right" arrow>
                  <Button
                    onClick={handleLogout}
                    sx={{
                      width: drawerOpen ? "calc(100% - 16px)" : 44,
                      minWidth: drawerOpen ? "auto" : 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: drawerOpen ? "flex-start" : "center",
                      mx: "auto",
                      px: drawerOpen ? 2 : 0,
                      py: 1,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 700,
                      color: "#EF4444",
                      borderRadius: "10px",
                      gap: drawerOpen ? 1.5 : 0,
                      minWidth: 0,
                      flex: "none !important",
                      mt: 1,
                      "&:hover": {
                        backgroundColor: mode === "dark" ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2",
                        color: "#DC2626",
                      },
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    {drawerOpen && "Log Out"}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </StyledDrawer>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              px: { xs: 1.5, sm: 2, md: 2.5 },
              pt: 1,
              pb: 2,
              width: {
                md: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)`,
              },
              textAlign: "left !important", // Strictly override global .App center rule
              "& .MuiTypography-root": { textAlign: "left" }, // Direct override for Mui Typography nested alignments
              transition: themeObj.transitions.create(["width", "margin"], {
                easing: themeObj.transitions.easing.sharp,
                duration: themeObj.transitions.duration.enteringScreen,
              }),
            }}
          >
            <Toolbar sx={{ minHeight: { xs: '48px !important', sm: '52px !important' }, height: { xs: '48px', sm: '52px' } }} />
            {currentView === "messages" || currentView === "mailHistory" ? (
              <MessageHistory />
            ) : currentView === "orders" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <CreateOrder
                  onSuccess={(order, targetView) => {
                    setCurrentView("ris_worklist");
                    setSelectedPatientForOrder(null);
                    fetchTodayWorkflow();
                  }}
                  onClose={() => {
                    setCurrentView("ris_worklist");
                    setSelectedPatientForOrder(null);
                  }}
                  onClearPatient={() => setSelectedPatientForOrder(null)}
                  preselectedPatient={selectedPatientForOrder}
                />
              </Container>
            ) : currentView === "editOrder" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <CreateOrder
                  editOrder={selectedOrderForEdit}
                  onSuccess={(order, targetView) => {
                    setCurrentView(targetView === undefined ? editOrderReturnView : targetView);
                    setSelectedOrderForEdit(null);
                    fetchTodayWorkflow();
                  }}
                  onClose={() => {
                    setCurrentView(editOrderReturnView);
                    setSelectedOrderForEdit(null);
                    setEditOrderReturnView('ris_worklist');
                  }}
                />
              </Container>
            ) : currentView === "ris_worklist" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <RISWorklist />
              </Container>
            ) : currentView === "scheduling" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <SchedulingBoard />
              </Container>
            ) : currentView === "rooms" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <RoomManagement />
              </Container>
            ) : currentView === "users" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <UserManagement />
              </Container>
            ) : currentView === "imaging" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <Imaging />
              </Container>
            ) : currentView === "reports" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <ReportsList />
              </Container>
            ) : currentView === "registerPatient" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <PatientRegistration 
                  onSuccess={() => {
                    setCurrentView("patients");
                    fetchTodayWorkflow();
                  }} 
                  onClose={() => setCurrentView("patients")}
                  onCreateOrder={(patient) => {
                    setSelectedPatientForOrder(patient);
                    setCurrentView("orders");
                    fetchTodayWorkflow();
                  }}
                />
              </Container>
            ) : currentView === "patientProfile" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <PatientProfileView 
                  patient={selectedPatientForProfile}
                  onBack={() => setCurrentView("patients")}
                  onCreateOrder={(p) => {
                    setSelectedPatientForOrder(p);
                    setCurrentView("orders");
                  }}
                  onViewOrder={(study) => handleViewDetails(study)}
                  setCurrentView={setCurrentView}
                />
              </Container>
            ) : currentView === "profile" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <ProfileSettings />
              </Container>
            ) : currentView === "settings" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <UserSettings />
              </Container>
            ) : currentView === "help" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                <HelpSupport variant="ris" />
              </Container>
            ) : currentView === "patients" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "100% !important",
                  px: 0,
                  textAlign: "left !important",
                  "& .MuiTypography-root": { textAlign: "left" },
                  pb: 2,
                }}
              >
                {/* Header Section */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.75,
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 0,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: mode === "dark" ? "#FFF" : "#0F172A",
                        fontSize: "1.1rem",
                        fontFamily: '"Satoshi", sans-serif',
                        letterSpacing: "-0.01em",
                        lineHeight: 1.2,
                      }}
                    >
                      Patients
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: textColorSecondary, fontSize: "0.75rem", fontWeight: 500, fontFamily: '"Satoshi", sans-serif' }}
                    >
                      Search and manage patient records
                    </Typography>
                  </Box>
                  {canRegisterPatients && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setCurrentView("registerPatient")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#007A6C",
                      color: "#FFFFFF",
                      px: 1.75,
                      height: 28,
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#005F54",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New Patient
                  </Button>
                  )}
                </Box>

                {/* Filters Row Card */}
                <Paper
                  sx={{
                    p: "6px 10px",
                    mb: 0.75,
                    border: cardBorder,
                    boxShadow: "none",
                    borderRadius: "8px",
                    bgcolor: cardBg,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: 1,
                      alignItems: "flex-end",
                      width: "100%",
                    }}
                  >
                    {/* Search Field */}
                    <Box sx={{ flex: { xs: "1 1 100%", md: "1.2 1 160px" }, minWidth: { xs: "100%", sm: "140px" }, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <TextField
                        placeholder="Search by name or patient ID..."
                        size="small"
                        value={patientSearch}
                        onChange={(e) => {
                          setPatientSearch(e.target.value);
                          setPatientsPage(0);
                        }}
                        inputRef={searchInputRef}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            bgcolor: cardBg,
                            height: "28px",
                            fontSize: "0.78rem",
                            "& fieldset": { borderColor: cardBorderColor },
                            "&:hover fieldset": { borderColor: cardBorderColor },
                            "&.Mui-focused fieldset": { borderColor: "#0D9488", borderWidth: "1.5px" },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "0.78rem",
                            color: textColorPrimary,
                            fontWeight: 600,
                            py: 0,
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ fontSize: 15, color: "#64748B" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Registered On Date Range */}
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 180px", md: "1 1 180px" }, minWidth: { xs: "100%", sm: "165px" }, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.68rem", textTransform: "none", lineHeight: 1 }}
                      >
                        Registered On
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          border: cardBorder,
                          borderRadius: "6px",
                          px: 0.75,
                          bgcolor: cardBg,
                          height: "28px",
                          width: "100%",
                          transition: "border-color 0.2s",
                          "&:hover": { borderColor: cardBorderColor },
                          "&:focus-within": { borderColor: "#0D9488", borderWidth: "1px" },
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <input
                          type="date"
                          value={patientStartDate}
                          onChange={(e) => { setPatientStartDate(e.target.value); setPatientsPage(0); }}
                          style={{ border: "none", outline: "none", background: "transparent", fontSize: "0.74rem", color: textColorPrimary, fontFamily: "inherit", fontWeight: 600, width: "100%", minWidth: 0 }}
                        />
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, px: 0.1, fontSize: "0.7rem", flexShrink: 0 }}>-</Typography>
                        <input
                          type="date"
                          value={patientEndDate}
                          onChange={(e) => { setPatientEndDate(e.target.value); setPatientsPage(0); }}
                          style={{ border: "none", outline: "none", background: "transparent", fontSize: "0.74rem", color: textColorPrimary, fontFamily: "inherit", fontWeight: 600, width: "100%", minWidth: 0 }}
                        />
                      </Box>
                    </Box>

                    {/* Last Visit Date Range */}
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 180px", md: "1 1 180px" }, minWidth: { xs: "100%", sm: "165px" }, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.68rem", textTransform: "none", lineHeight: 1 }}
                      >
                        Last Visit
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          border: cardBorder,
                          borderRadius: "6px",
                          px: 0.75,
                          bgcolor: cardBg,
                          height: "28px",
                          width: "100%",
                          transition: "border-color 0.2s",
                          "&:hover": { borderColor: cardBorderColor },
                          "&:focus-within": { borderColor: "#0D9488", borderWidth: "1px" },
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <input
                          type="date"
                          value={lastVisitStartDate}
                          onChange={(e) => { setLastVisitStartDate(e.target.value); setPatientsPage(0); }}
                          style={{ border: "none", outline: "none", background: "transparent", fontSize: "0.74rem", color: textColorPrimary, fontFamily: "inherit", fontWeight: 600, width: "100%", minWidth: 0 }}
                        />
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, px: 0.1, fontSize: "0.7rem", flexShrink: 0 }}>-</Typography>
                        <input
                          type="date"
                          value={lastVisitEndDate}
                          onChange={(e) => { setLastVisitEndDate(e.target.value); setPatientsPage(0); }}
                          style={{ border: "none", outline: "none", background: "transparent", fontSize: "0.74rem", color: textColorPrimary, fontFamily: "inherit", fontWeight: 600, width: "100%", minWidth: 0 }}
                        />
                      </Box>
                    </Box>

                    {/* Gender Dropdown */}
                    <Box sx={{ flex: "0 0 85px", width: "85px", minWidth: "80px", display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.68rem", lineHeight: 1 }}
                      >
                        Gender
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={patientGender}
                          onChange={(e) => {
                            setPatientGender(e.target.value);
                            setPatientsPage(0);
                          }}
                          sx={{
                            borderRadius: "6px",
                            bgcolor: cardBg,
                            height: "28px",
                            fontWeight: 600,
                            fontSize: "0.78rem",
                            color: textColorPrimary,
                            "& .MuiSelect-select": { py: "2px", px: "6px" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: cardBorderColor },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: cardBorderColor },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0D9488", borderWidth: "1.5px" },
                          }}
                        >
                          <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                          <MenuItem value="Male" sx={{ fontSize: "0.78rem" }}>Male</MenuItem>
                          <MenuItem value="Female" sx={{ fontSize: "0.78rem" }}>Female</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Buttons Stack */}
                    <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0, height: "28px" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setPatientSearch("");
                          setPatientGender("All");
                          setPatientStartDate("");
                          setPatientEndDate("");
                          setLastVisitStartDate("");
                          setLastVisitEndDate("");
                          setPatientStatus("All");
                          setPatientAgeRange("All");
                          setPatientsPage(0);
                        }}
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          color: textColorSecondary,
                          borderColor: cardBorderColor,
                          fontWeight: 700,
                          borderRadius: "6px",
                          px: 1.25,
                          height: "100%",
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover": {
                            borderColor: cardBorderColor,
                            backgroundColor: hoverBgSubtle,
                          },
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          fetchPatients(
                            0,
                            patientsRowsPerPage,
                            patientSearch,
                            patientGender,
                          );
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderColor: "#0D9488",
                          color: "#0D9488",
                          borderRadius: "6px",
                          px: 1.25,
                          height: "100%",
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover": {
                            borderColor: "#0D9488",
                            backgroundColor: "#F0FDFA",
                          },
                        }}
                      >
                        Filter
                      </Button>
                    </Box>
                  </Box>
                </Paper>

                {/* Total count and Export panel */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: textColorPrimary, fontSize: "0.8rem" }}
                  >
                    Total Patients: {filteredPatientsByTab.length}
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: textColorPrimary,
                      borderColor: cardBorderColor,
                      borderRadius: "6px",
                      px: 1.25,
                      height: 26,
                      fontSize: "0.72rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        borderColor: cardBorderColor,
                        backgroundColor: hoverBgSubtle,
                      },
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </Box>

                {/* Patient Table Panel */}
                <Paper
                  sx={{
                    border: cardBorder,
                    boxShadow: "none",
                    borderRadius: "8px",
                    overflow: "hidden",
                    bgcolor: cardBg,
                    width: "100%",
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: headerBg }}>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Patient ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Age
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Gender
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Registered On
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                            Last Visit
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '4px !important', px: '8px !important', fontSize: "0.7rem", borderBottom: cardBorder, textAlign: "right", textTransform: 'uppercase' }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {patientsLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: "center", py: 3 }}>
                              <CircularProgress size={22} sx={{ color: "#0D9488" }} />
                            </TableCell>
                          </TableRow>
                        ) : filteredPatientsByTab.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: "center", py: 3, color: "#64748B", fontSize: "0.78rem" }}>
                              No patient records found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredPatientsByTab
                            .slice(
                              patientsPage * patientsRowsPerPage,
                              (patientsPage + 1) * patientsRowsPerPage
                            )
                            .map((p, index) => {
                              const patientName =
                                p.firstName && p.lastName
                                  ? `${p.firstName} ${p.lastName}`
                                  : p.name || p.patientName || "Unknown Patient";

                              const gender = p.sex || p.patientSex || p.gender || "";
                              const age = p.birthDate ? calculateAge(p.birthDate) : "-";

                              // Registered On Formatting
                              const registeredOn = new Date(p.createdAt || p.registeredOn || new Date()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

                              return (
                                <TableRow key={p._id || index} hover sx={{ height: '30px', "&:hover": { bgcolor: `${hoverBgSubtle} !important` } }}>
                                  <TableCell sx={{ fontSize: "0.74rem", color: textColorSecondary, fontWeight: 600, py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {p.mrn || p.patientId || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.74rem", color: textColorPrimary, fontWeight: 700, py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {patientName}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.74rem", color: textColorSecondary, fontWeight: 600, py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {age}
                                  </TableCell>
                                  <TableCell sx={{ py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {gender === "M" || gender === "Male" ? (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: 0.75,
                                          height: 17,
                                          borderRadius: "4px",
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                          bgcolor: isDark ? "rgba(37, 99, 235, 0.2)" : "#EFF6FF",
                                          color: isDark ? "#60A5FA" : "#2563EB",
                                        }}
                                      >
                                        M
                                      </Box>
                                    ) : gender === "F" || gender === "Female" ? (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: 0.75,
                                          height: 17,
                                          borderRadius: "4px",
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                          bgcolor: isDark ? "rgba(219, 39, 119, 0.2)" : "#FDF2F8",
                                          color: isDark ? "#F472B6" : "#DB2777",
                                        }}
                                      >
                                        F
                                      </Box>
                                    ) : (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: 0.75,
                                          height: 17,
                                          borderRadius: "4px",
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                          bgcolor: isDark ? "rgba(100, 116, 139, 0.2)" : "#F1F5F9",
                                          color: textColorSecondary,
                                        }}
                                      >
                                        -
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.74rem", color: textColorSecondary, fontWeight: 500, py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {registeredOn}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.74rem", py: '2px !important', px: '8px !important', borderBottom: cardBorder }}>
                                    {p.lastVisit ? (
                                      <Typography sx={{ fontSize: "0.74rem", color: textColorPrimary, fontWeight: 500 }}>
                                        {new Date(p.lastVisit).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </Typography>
                                    ) : (
                                      <Typography sx={{ fontSize: "0.72rem", color: textColorSecondary, fontStyle: "italic" }}>No visits</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ py: '2px !important', px: '8px !important', borderBottom: cardBorder, textAlign: "right" }}>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                                      {/* View Patient Profile Page Eye button */}
                                      <Tooltip title="View full patient profile page" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setSelectedPatientForProfile(p);
                                            setCurrentView("patientProfile");
                                          }}
                                          sx={{
                                            color: "#0D9488",
                                            border: cardBorder,
                                            borderRadius: "4px",
                                            width: 24,
                                            height: 24,
                                            p: 0,
                                            "&:hover": {
                                              backgroundColor: hoverTealSubtle,
                                            }
                                          }}
                                        >
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                          </svg>
                                        </IconButton>
                                      </Tooltip>

                                      {/* View Patient Details Silhouette button */}
                                      <Tooltip title="View patient registration details" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenViewPatient(p)}
                                          sx={{
                                            color: "#0D9488",
                                            border: cardBorder,
                                            borderRadius: "4px",
                                            width: 24,
                                            height: 24,
                                            p: 0,
                                            "&:hover": {
                                              backgroundColor: hoverTealSubtle,
                                            }
                                          }}
                                        >
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                          </svg>
                                        </IconButton>
                                      </Tooltip>

                                      {/* Create Order document-plus button */}
                                      <Tooltip title="Create a new imaging order for this patient" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setSelectedPatientForOrder(p);
                                            setCurrentView("orders");
                                          }}
                                          sx={{
                                            color: "#0D9488",
                                            border: cardBorder,
                                            borderRadius: "4px",
                                            width: 24,
                                            height: 24,
                                            p: 0,
                                            "&:hover": {
                                              backgroundColor: hoverTealSubtle,
                                            }
                                          }}
                                        >
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="12" y1="18" x2="12" y2="12" />
                                            <line x1="9" y1="15" x2="15" y2="15" />
                                          </svg>
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination Section Matching Mockup exactly */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: "4px 10px",
                      borderTop: `1px solid ${cardBorderColor}`,
                      flexWrap: "wrap",
                      gap: 1,
                      bgcolor: cardBg,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.75rem" }}>
                        Showing {filteredPatientsByTab.length > 0 ? (patientsPage * patientsRowsPerPage) + 1 : 0} to {Math.min((patientsPage + 1) * patientsRowsPerPage, filteredPatientsByTab.length)} of {filteredPatientsByTab.length} entries
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600 }}>
                          Rows:
                        </Typography>
                        <Select
                          size="small"
                          value={patientsRowsPerPage}
                          onChange={(e) => {
                            setPatientsRowsPerPage(Number(e.target.value));
                            setPatientsPage(0);
                          }}
                          sx={{
                            height: 24,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: textColorPrimary,
                            "& .MuiSelect-select": { py: "2px", px: "6px" },
                            "& fieldset": { borderColor: cardBorderColor },
                          }}
                        >
                          {[10, 15, 20, 25, 50, 100].map((num) => (
                            <MenuItem key={num} value={num} sx={{ fontSize: "0.72rem" }}>{num}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {/* Prev Button */}
                      <IconButton
                        size="small"
                        disabled={patientsPage === 0}
                        onClick={() => setPatientsPage(patientsPage - 1)}
                        sx={{
                          border: cardBorder,
                          borderRadius: "6px",
                          width: 26,
                          height: 26,
                          color: textColorSecondary,
                          "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </IconButton>

                      {/* Page numbers with smart ellipsis */}
                      {(() => {
                        const totalPages = Math.ceil(filteredPatientsByTab.length / patientsRowsPerPage) || 1;
                        const pageNumbers = [];
                        
                        for (let i = 0; i < totalPages; i++) {
                          if (
                            i === 0 ||
                            i === totalPages - 1 ||
                            Math.abs(i - patientsPage) <= 1
                          ) {
                            pageNumbers.push(i);
                          } else if (
                            (i === 1 && patientsPage > 2) ||
                            (i === totalPages - 2 && patientsPage < totalPages - 3)
                          ) {
                            pageNumbers.push('ellipsis');
                          }
                        }
                        
                        const uniquePageNumbers = pageNumbers.filter((item, index, self) => 
                          item !== 'ellipsis' || self.indexOf('ellipsis') === index
                        );

                        return uniquePageNumbers.map((pNum, idx) => {
                          if (pNum === 'ellipsis') {
                            return (
                              <Typography key={`ellipsis-${idx}`} sx={{ px: 0.5, color: textColorSecondary, fontWeight: 700, fontSize: "0.75rem" }}>
                                ...
                              </Typography>
                            );
                          }
                          
                          const isActive = patientsPage === pNum;
                          return (
                            <Button
                              key={pNum}
                              size="small"
                              onClick={() => setPatientsPage(pNum)}
                              sx={{
                                minWidth: 24,
                                height: 24,
                                p: 0,
                                borderRadius: "6px",
                                bgcolor: isActive ? "#0D9488" : "transparent",
                                color: isActive ? "#FFFFFF" : textColorPrimary,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                border: isActive ? "none" : cardBorder,
                                "&:hover": {
                                  bgcolor: isActive ? "#0D9488" : hoverBgSubtle,
                                }
                              }}
                            >
                              {pNum + 1}
                            </Button>
                          );
                        });
                      })()}

                      {/* Next Button */}
                      <IconButton
                        size="small"
                        disabled={(patientsPage + 1) * patientsRowsPerPage >= filteredPatientsByTab.length}
                        onClick={() => setPatientsPage(patientsPage + 1)}
                        sx={{
                          border: cardBorder,
                          borderRadius: "6px",
                          width: 26,
                          height: 26,
                          color: textColorSecondary,
                          "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Container>
            ) : currentView === "dashboard" ? (
              <Container
                maxWidth={false}
                disableGutters
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "100% !important",
                  px: 0,
                  textAlign: "left !important",
                  pb: 2,
                }}
              >
                {/* Compact Top Summary & Quick Actions Strip */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mt: 0.5,
                    mb: 1.5,
                  }}
                >
                  {/* Left: 3 Compact Metric Pills */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {/* Metric 1: Today's Orders */}
                    <Paper
                      elevation={0}
                      onClick={() => setCurrentView("ris_worklist")}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        border: cardBorder,
                        bgcolor: cardBg,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#0D9488", bgcolor: hoverTealSubtle },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          color: "#0D9488",
                          bgcolor: isDark ? "rgba(13,148,136,0.15)" : "#F0FDFA",
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                        </svg>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 800, color: "#00665C", fontSize: "1rem", lineHeight: 1 }}>
                          {todayWorkflowStudies.length}
                        </Typography>
                        <Typography sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem" }}>
                          Today's Orders
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Metric 2: Pending Imaging */}
                    <Paper
                      elevation={0}
                      onClick={() => setCurrentView("ris_worklist")}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        border: cardBorder,
                        bgcolor: cardBg,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#D97706", bgcolor: isDark ? "rgba(217,119,6,0.1)" : "#FFFBEB" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          color: "#D97706",
                          bgcolor: isDark ? "rgba(217,119,6,0.15)" : "#FFFBEB",
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 3h14M5 21h14M19 3v4c0 2.2-1.8 4-4 4H9c-2.2 0-4-1.8-4-4V3M5 21v-4c0-2.2 1.8-4 4-4h6c2.2 0 4 1.8 4 4v4" />
                        </svg>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 800, color: "#D97706", fontSize: "1rem", lineHeight: 1 }}>
                          {todayWorkflowStudies.filter(s => {
                            const lbl = getStudyStatusInfo(s).label;
                            return lbl === "Arrived" || lbl === "Scheduled" || lbl === "Registered" || lbl === "In Exam Room";
                          }).length}
                        </Typography>
                        <Typography sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem" }}>
                          Pending Imaging
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Metric 3: Completed Studies */}
                    <Paper
                      elevation={0}
                      onClick={() => setCurrentView("ris_worklist")}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        border: cardBorder,
                        bgcolor: cardBg,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#2563EB", bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          color: "#2563EB",
                          bgcolor: isDark ? "rgba(37,99,235,0.15)" : "#EFF6FF",
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 800, color: "#2563EB", fontSize: "1rem", lineHeight: 1 }}>
                          {todayWorkflowStudies.filter(s => getStudyStatusInfo(s).label === "Completed").length}
                        </Typography>
                        <Typography sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem" }}>
                          Completed
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Right: Quick Action Buttons */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {canRegisterPatients && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCurrentView("registerPatient")}
                        startIcon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="17" y1="11" x2="23" y2="11" />
                          </svg>
                        }
                        sx={{
                          height: 28,
                          px: 1.5,
                          borderRadius: "6px",
                          borderColor: "#0D9488",
                          color: "#0D9488",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "none",
                          "&:hover": { borderColor: "#00665C", bgcolor: hoverTealSubtle }
                        }}
                      >
                        Register Patient
                      </Button>
                    )}

                    {canCreateOrders && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setCurrentView("orders")}
                        startIcon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                            <line x1="12" y1="11" x2="12" y2="17" />
                            <line x1="9" y1="14" x2="15" y2="14" />
                          </svg>
                        }
                        sx={{
                          height: 28,
                          px: 1.5,
                          borderRadius: "6px",
                          bgcolor: "#007A6C",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "none",
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#005F54", boxShadow: "none" }
                        }}
                      >
                        Create Order
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Today's Worklist Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: "10px 14px",
                    borderRadius: "8px",
                    border: cardBorder,
                    bgcolor: cardBg,
                    boxShadow: "none",
                  }}
                >
                  {/* Section Title and Search/Filter Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.25,
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: textColorPrimary, fontSize: "1.05rem" }}>
                        Today's Worklist
                      </Typography>
                      <Chip
                        size="small"
                        label={`${todayWorkflowStudies.length} orders`}
                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: isDark ? "rgba(13,148,136,0.15)" : "#F0FDFA", color: "#0D9488" }}
                      />
                    </Box>

                    {/* Controls container */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
                      {/* Search Bar pill matching mockup */}
                      <TextField
                        size="small"
                        placeholder="Search patient, ID or order..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        sx={{
                          width: { xs: "100%", sm: 220 },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            backgroundColor: inputBg,
                            border: cardBorder,
                            height: 30,
                            px: 1,
                            fontSize: "0.78rem",
                            "& fieldset": { border: "none" },
                            "&.Mui-focused": {
                              backgroundColor: cardBg,
                              border: "1px solid #0D9488",
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: "#94A3B8", fontSize: 16 }} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* Filter Button */}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                          </svg>
                        }
                        sx={{
                          borderRadius: "6px",
                          borderColor: cardBorderColor,
                          color: textColorPrimary,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "none",
                          height: 30,
                          px: 1.5,
                          "&:hover": {
                            borderColor: cardBorderColor,
                            bgcolor: hoverBgSubtle
                          }
                        }}
                      >
                        Filter
                      </Button>
                    </Box>
                  </Box>

                  {/* Bulk send toolbar — appears when studies are selected */}
                  {selectedStudiesForBulk.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, mb: 1, bgcolor: '#EFF6FF', borderRadius: '6px', border: '1px solid #BFDBFE' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1D4ED8', flex: 1 }}>
                        {selectedStudiesForBulk.length} {selectedStudiesForBulk.length === 1 ? 'study' : 'studies'} selected
                      </Typography>
                      <Button size="small" onClick={handleClearBulkSelection} sx={{ color: '#6B7280', fontWeight: 600, textTransform: 'none', height: 24, fontSize: '0.72rem' }}>
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DoneAll sx={{ fontSize: '14px !important' }} />}
                        onClick={handleOpenBulkSend}
                        disabled={selectedStudiesForBulk.length > 10}
                        sx={{ bgcolor: '#0D9488', '&:hover': { bgcolor: '#00665C' }, textTransform: 'none', fontWeight: 700, borderRadius: '6px', height: 24, fontSize: '0.72rem' }}
                      >
                        {selectedStudiesForBulk.length > 10 ? 'Max 10 studies' : `Bulk Send (${selectedStudiesForBulk.length})`}
                      </Button>
                    </Box>
                  )}

                  {/* Table Container */}
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: headerBg }}>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ borderBottom: cardBorder, py: '4px !important', px: '6px !important' }}>
                            <Checkbox
                              size="small"
                              indeterminate={selectedStudiesForBulk.length > 0 && selectedStudiesForBulk.length < todayWorkflowStudies.filter(s => !s.isPatientOnly && s._id && !String(s._id).startsWith('patient-')).length}
                              checked={todayWorkflowStudies.filter(s => !s.isPatientOnly && s._id && !String(s._id).startsWith('patient-')).length > 0 && selectedStudiesForBulk.length === todayWorkflowStudies.filter(s => !s.isPatientOnly && s._id && !String(s._id).startsWith('patient-')).length}
                              onChange={() => {
                                const emailableStudies = todayWorkflowStudies.filter(s => !s.isPatientOnly && s._id && !String(s._id).startsWith('patient-'));
                                if (selectedStudiesForBulk.length === emailableStudies.length) {
                                  setSelectedStudiesForBulk([]);
                                } else {
                                  setSelectedStudiesForBulk([...emailableStudies]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Patient</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Patient ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Modality</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Exam</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Order Time</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {todayWorkflowLoading ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <CircularProgress size={24} sx={{ color: "#0D9488" }} />
                            </TableCell>
                          </TableRow>
                        ) : todayWorkflowStudies.length > 0 ? (
                          // Client-side search filtering
                          todayWorkflowStudies
                            .filter(study => {
                              const q = (filters.search || "").toLowerCase();
                              const patName = formatPatientName(study.patientName || study.patient?.patientName || "").toLowerCase();
                              const patId = String(study.patientId || study.patientMrn || "").toLowerCase();
                              const exam = String(study.studyDescription || "").toLowerCase();
                              const modality = String(study.modality || "").toLowerCase();
                              return patName.includes(q) || patId.includes(q) || exam.includes(q) || modality.includes(q);
                            })
                            // Client-side pagination
                            .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                            .map((study, idx) => {
                              const patientName = formatPatientName(study.patientName || study.patient?.patientName || "");
                              const patientId = study.patientId || study.patientMrn || "-";
                              const modality = study.modality || '-';
                              const exam = study.studyDescription || study.procedureDescription || (modality !== '-' ? `${modality} Study` : '-');
                              const createdStr = (study.createdAt || study.studyDate) ? new Date(study.createdAt || study.studyDate).toLocaleDateString('en-GB') : '-';

                              // Time extraction and format matching mockup
                              const timeStr = study.studyTime
                                ? (() => {
                                    const t = study.studyTime;
                                    const h = parseInt(t.substring(0, 2), 10);
                                    const m = t.substring(2, 4) || "00";
                                    const suffix = h >= 12 ? "PM" : "AM";
                                    const displayH = h % 12 === 0 ? 12 : h % 12;
                                    return `${String(displayH).padStart(2, "0")}:${m} ${suffix}`;
                                  })()
                                : study.studyDate
                                  ? new Date(study.studyDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                  : "09:15 AM";

                              const statusInfo = getStudyStatusInfo(study);
                              const statusLabel = statusInfo.label;

                              // Map statuses to mockup colors and text
                              let badgeText = "New Order";
                              let badgeColors = isDark 
                                ? { bg: "rgba(37, 99, 235, 0.2)", fg: "#60A5FA" } 
                                : { bg: "#EFF6FF", fg: "#2563EB" }; // light blue

                              if (statusLabel === "In Exam Room" || statusLabel === "In Exam" || statusLabel === "ARRIVED") {
                                badgeText = "In Progress";
                                badgeColors = isDark 
                                  ? { bg: "rgba(245, 158, 11, 0.2)", fg: "#F59E0B" } 
                                  : { bg: "#FEF3C7", fg: "#D97706" }; // light orange
                              } else if (statusLabel === "Completed" || statusLabel === "Awaiting Report") {
                                badgeText = "Completed";
                                badgeColors = isDark 
                                  ? { bg: "rgba(16, 185, 129, 0.2)", fg: "#34D399" } 
                                  : { bg: "#ECFDF5", fg: "#059669" }; // light green
                              }

                              const canEmail = !study.isPatientOnly && study._id && !String(study._id).startsWith('patient-') && !String(study._id).startsWith('order-');
                              return (
                                <TableRow key={study._id || idx} hover selected={canEmail && selectedStudiesForBulk.some((s) => s._id === study._id)} sx={{ height: '34px', "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: `${hoverBgSubtle} !important` } }}>
                                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ py: '3px !important', px: '6px !important' }}>
                                    {canEmail && (
                                      <Checkbox
                                        size="small"
                                        checked={selectedStudiesForBulk.some((s) => s._id === study._id)}
                                        onChange={() => handleToggleSelectStudy(study)}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorSecondary, fontWeight: 500, borderBottom: cardBorder }}>{createdStr}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorPrimary, fontWeight: 600, borderBottom: cardBorder }}>{patientName}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorSecondary, fontWeight: 500, borderBottom: cardBorder }}>{patientId}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorPrimary, fontWeight: 700, borderBottom: cardBorder }}>{study.isPatientOnly ? '-' : modality}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorSecondary, fontWeight: 500, borderBottom: cardBorder }}>{study.isPatientOnly ? '-' : exam}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important', fontSize: '0.75rem', color: textColorSecondary, fontWeight: 500, borderBottom: cardBorder }}>{study.isPatientOnly ? '-' : timeStr}</TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important' }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 1,
                                        height: 20,
                                        borderRadius: "4px",
                                        bgcolor: badgeColors.bg,
                                        color: badgeColors.fg,
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        whiteSpace: "nowrap"
                                      }}
                                    >
                                        {study.isPatientOnly ? "New Patient" : study.isOrderOnly ? (study.orderStatus || 'New Order') : badgeText}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ py: '3px !important', px: '8px !important' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {study.isPatientOnly ? (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <Tooltip title="View patient details and registration information" arrow>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              onClick={() => handleViewPatientDetails(study)}
                                              sx={{
                                                textTransform: "none",
                                                borderRadius: "4px",
                                                borderColor: "#0D9488",
                                                color: "#0D9488",
                                                fontWeight: 700,
                                                fontSize: "0.72rem",
                                                height: 24,
                                                px: 1,
                                                "&:hover": {
                                                  borderColor: "#00665C",
                                                  bgcolor: hoverTealSubtle,
                                                }
                                              }}
                                            >
                                              View Details
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title="Create a new imaging order for this patient" arrow>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              onClick={() => {
                                                handleCreateOrderForPatient(study);
                                              }}
                                              sx={{
                                                textTransform: "none",
                                                borderRadius: "4px",
                                                borderColor: "#0D9488",
                                                color: "#0D9488",
                                                fontWeight: 700,
                                                fontSize: "0.72rem",
                                                height: 24,
                                                px: 1,
                                                "&:hover": {
                                                  borderColor: "#00665C",
                                                  bgcolor: hoverTealSubtle,
                                                }
                                              }}
                                            >
                                              Create Order
                                            </Button>
                                          </Tooltip>
                                        </Box>
                                      ) : badgeText === "In Progress" ? (
                                        <Tooltip title="Upload images or update the status of this study" arrow>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleWorkflowUpload(study)}
                                            sx={{
                                              textTransform: "none",
                                              borderRadius: "4px",
                                              borderColor: "#0D9488",
                                              color: "#0D9488",
                                              fontWeight: 700,
                                              fontSize: "0.72rem",
                                              height: 24,
                                              px: 1,
                                              "&:hover": {
                                                borderColor: "#00665C",
                                                bgcolor: hoverTealSubtle,
                                              }
                                            }}
                                          >
                                            Update
                                          </Button>
                                        </Tooltip>
                                      ) : (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <Tooltip title={badgeText === "Completed" ? "View the imaging study in the OHIF viewer" : "View the full order details and patient information"} arrow>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              onClick={() => badgeText === "Completed" ? handleViewImages(study) : handleViewDetails(study)}
                                              sx={{
                                                textTransform: "none",
                                                borderRadius: "4px",
                                                borderColor: "#0D9488",
                                                color: "#0D9488",
                                                fontWeight: 700,
                                                fontSize: "0.72rem",
                                                height: 24,
                                                px: 1,
                                                "&:hover": {
                                                  borderColor: "#00665C",
                                                  bgcolor: hoverTealSubtle,
                                                }
                                              }}
                                            >
                                              {badgeText === "Completed" ? "View Image" : "View Order"}
                                            </Button>
                                          </Tooltip>
                                          {badgeText !== "Completed" && (
                                            <Tooltip title="Edit or update the details of this imaging order" arrow>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={async () => {
                                                  if (study.isOrderOnly && study.orderDbId) {
                                                    try {
                                                      const res = await orderService.getById(study.orderDbId);
                                                      const fullOrder = res?.data?.order || res?.data || res;
                                                      setSelectedOrderForEdit(fullOrder);
                                                      setEditOrderReturnView('dashboard');
                                                      setCurrentView('editOrder');
                                                    } catch (e) {
                                                      console.error('Failed to load order for editing:', e);
                                                    }
                                                  } else {
                                                    handleViewDetails(study);
                                                  }
                                                }}
                                                sx={{
                                                  textTransform: "none",
                                                  borderRadius: "4px",
                                                  borderColor: "#0D9488",
                                                  color: "#0D9488",
                                                  fontWeight: 700,
                                                  fontSize: "0.72rem",
                                                  height: 24,
                                                  px: 1,
                                                  "&:hover": {
                                                    borderColor: "#00665C",
                                                    bgcolor: hoverTealSubtle,
                                                  }
                                                }}
                                              >
                                                Edit Order
                                              </Button>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      )}
                                      <Tooltip title="Patient History">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenHistory(study)}
                                          sx={{ color: "#7C3AED", width: 24, height: 24, p: 0, "&:hover": { bgcolor: "rgba(124,58,237,0.08)" } }}
                                        >
                                          <HistoryIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      {canEmail && (
                                        <Tooltip title="Send Study via Email">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleSendEmail(study)}
                                            sx={{ color: "#0D9488", width: 24, height: 24, p: 0, "&:hover": { bgcolor: hoverTealSubtle } }}
                                          >
                                            <Send sx={{ fontSize: 15 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: "0.8rem" }}>
                                No studies found for today.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination Section Matching Mockup exactly */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                      pt: 1,
                      borderTop: `1px solid ${cardBorderColor}`,
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2" sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.75rem" }}>
                        Showing {todayWorkflowStudies.length > 0 ? (page * rowsPerPage) + 1 : 0} to {Math.min((page + 1) * rowsPerPage, todayWorkflowStudies.length)} of {todayWorkflowStudies.length} entries
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 600 }}>
                          Rows:
                        </Typography>
                        <Select
                          size="small"
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(0);
                          }}
                          sx={{
                            height: 26,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: textColorPrimary,
                            "& .MuiSelect-select": { py: "2px", px: "6px" },
                            "& fieldset": { borderColor: cardBorderColor },
                          }}
                        >
                          {[10, 20, 25, 50, 100].map((num) => (
                            <MenuItem key={num} value={num} sx={{ fontSize: "0.75rem" }}>{num}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {/* Prev Button */}
                      <IconButton
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                        sx={{
                          border: cardBorder,
                          borderRadius: "6px",
                          width: 28,
                          height: 28,
                          color: textColorSecondary,
                          "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </IconButton>

                      {/* Page numbers */}
                      {(() => {
                        const totalPages = Math.ceil(todayWorkflowStudies.length / rowsPerPage) || 1;
                        const pageNumbers = [];
                        for (let i = 0; i < totalPages; i++) {
                          if (i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1) {
                            pageNumbers.push(i);
                          } else if ((i === 1 && page > 2) || (i === totalPages - 2 && page < totalPages - 3)) {
                            pageNumbers.push('ellipsis');
                          }
                        }
                        const uniquePageNumbers = pageNumbers.filter((item, index, self) => item !== 'ellipsis' || self.indexOf('ellipsis') === index);

                        return uniquePageNumbers.map((pNum, idx) => {
                          if (pNum === 'ellipsis') {
                            return <Typography key={`ell-${idx}`} sx={{ px: 0.5, color: textColorSecondary, fontSize: "0.75rem" }}>...</Typography>;
                          }
                          const isActive = page === pNum;
                          return (
                            <Button
                              key={pNum}
                              size="small"
                              onClick={() => setPage(pNum)}
                              sx={{
                                minWidth: 26,
                                height: 26,
                                p: 0,
                                borderRadius: "6px",
                                bgcolor: isActive ? "#0D9488" : "transparent",
                                color: isActive ? "#FFFFFF" : textColorPrimary,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                border: isActive ? "none" : cardBorder,
                                "&:hover": {
                                  bgcolor: isActive ? "#0D9488" : hoverBgSubtle,
                                }
                              }}
                            >
                              {pNum + 1}
                            </Button>
                          );
                        });
                      })()}

                      {/* Next Button */}
                      <IconButton
                        size="small"
                        disabled={(page + 1) * rowsPerPage >= todayWorkflowStudies.length}
                        onClick={() => setPage(page + 1)}
                        sx={{
                          border: cardBorder,
                          borderRadius: "6px",
                          width: 28,
                          height: 28,
                          color: textColorSecondary,
                          "&:disabled": { color: isDark ? "rgba(255, 255, 255, 0.2)" : "#CBD5E1" }
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Container>
            ) : (
              <Container
                maxWidth={false}
                disableGutters
                sx={{ position: "relative", width: "100%", maxWidth: "100% !important", px: 0 }}
              >
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      background:
                        themeObj.palette.mode === "dark"
                          ? "#1E1E1E"
                          : "#FFFFFF",
                    }}
                    onClose={() => setError("")}
                  >
                    {error}
                  </Alert>
                )}

                {uploadStatus && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      background:
                        themeObj.palette.mode === "dark"
                          ? "#1E1E1E"
                          : "#FFFFFF",
                    }}
                    onClose={() => setUploadStatus("")}
                  >
                    {uploadStatus}
                  </Alert>
                )}

                <GlassPaper sx={{ p: isSmallMobile ? 2 : 3, mb: 3 }}>
                  <Grid
                    container
                    spacing={isSmallMobile ? 1 : 2}
                    alignItems="center"
                  >
                    <Grid item xs={12}>
                      {location.search.includes("facilityId") && (
                        <Box
                          mb={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Alert
                            severity="info"
                            sx={{ flexGrow: 1, borderRadius: "12px" }}
                            icon={<Business />}
                          >
                            Viewing images for facility:{" "}
                            <strong>
                              {new URLSearchParams(location.search).get(
                                "facilityName",
                              ) || "Selected Facility"}
                            </strong>
                          </Alert>
                          {user?.role === "admin" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={handleBackToAdmin}
                              sx={{ ml: 1 }}
                            >
                              Back to Admin
                            </Button>
                          )}
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Tooltip title="Search by patient name (Last First or Last^First), patient ID, or study description">
                        <div>
                          <TextField
                            fullWidth
                            size="small"
                            label={t("dashboard.searchPatientsStudies")}
                            placeholder="e.g., Smith^John, 12345, or CT Abdomen"
                            variant="outlined"
                            value={filters.search}
                            onChange={(e) =>
                              setFilters({ ...filters, search: e.target.value })
                            }
                            inputRef={searchInputRef}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      try {
                                        searchInputRef.current?.focus();
                                      } catch (e) {}
                                      try {
                                        fetchScanBoardPage(
                                          0,
                                          rowsPerPage,
                                          filters.search,
                                          filters.modality,
                                        );
                                      } catch (e) {
                                        // fetchScanBoardPage may not be ready during very early renders
                                      }
                                    }}
                                  >
                                    <Search
                                      sx={{ mr: 1, color: "text.secondary" }}
                                    />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setFilters({ ...filters, search: "" })
                                  }
                                  sx={{ mr: -0.5 }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  borderColor: teslaColors.dark.primary,
                                },
                                "&.Mui-focused": {
                                  borderColor: teslaColors.dark.secondary,
                                  boxShadow: `0 0 0 2px ${alpha(teslaColors.dark.primary, 0.2)}`,
                                },
                              },
                            }}
                          />
                        </div>
                      </Tooltip>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t("dashboard.modality")}</InputLabel>
                        <Select
                          value={filters.modality}
                          label={t("dashboard.modality")}
                          onChange={(e) =>
                            setFilters({ ...filters, modality: e.target.value })
                          }
                          sx={{ borderRadius: "12px" }}
                        >
                          <MenuItem value="all">
                            {t("dashboard.allModalities")}
                          </MenuItem>
                          <MenuItem value="CT">CT</MenuItem>
                          <MenuItem value="MR">MRI</MenuItem>
                          <MenuItem value="XR">X-Ray</MenuItem>
                          <MenuItem value="US">Ultrasound</MenuItem>
                          <MenuItem value="CR">CR</MenuItem>
                          <MenuItem value="DR">DR</MenuItem>
                          <MenuItem value="MG">Mammography</MenuItem>
                          <MenuItem value="OT">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <Stack direction="column" spacing={1}>
                        <Button
                          variant="outlined"
                          startIcon={isSmallMobile ? null : <Refresh />}
                          onClick={() => {
                            fetchScanBoardPage(
                              page,
                              rowsPerPage,
                              debouncedSearch,
                              filters.modality,
                            );
                            fetchStudyStats();
                          }}
                          disabled={loading}
                          sx={{
                            borderRadius: "12px",
                            borderColor: teslaColors.dark.primary,
                            color: teslaColors.dark.primary,
                            "&:hover": {
                              borderColor: teslaColors.dark.secondary,
                              backgroundColor: alpha(
                                teslaColors.dark.primary,
                                0.1,
                              ),
                            },
                          }}
                          fullWidth
                        >
                          {isSmallMobile ? <Refresh /> : t("common.refresh")}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={
                            syncing ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Refresh />
                            )
                          }
                          onClick={handleSync}
                          disabled={syncing || loading}
                          sx={{
                            borderRadius: "12px",
                            borderColor: "#43e97b",
                            color: "#43e97b",
                            "&:hover": {
                              borderColor: "#38f9d7",
                              backgroundColor: alpha("#43e97b", 0.1),
                            },
                          }}
                          fullWidth
                        >
                          {syncing ? "Syncing..." : "Sync Orthanc"}
                        </Button>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <GlowButton
                          startIcon={isMobile ? null : <CloudUpload />}
                          onClick={() => setUploadDialogOpen(true)}
                          fullWidth
                        >
                          {isMobile ? (
                            <CloudUpload />
                          ) : (
                            t("dashboard.uploadStudies")
                          )}
                        </GlowButton>

                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.7,
                            fontSize: isSmallMobile ? "0.7rem" : "0.875rem",
                            textAlign: "center",
                          }}
                        >
                          {loading && !serverStats ? (
                            <>Loading stats...</>
                          ) : (
                            <>{serverStats?.totalStudies || 0} studies</>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </GlassPaper>

                <GlassPaper ref={studiesRef} sx={{ p: isSmallMobile ? 2 : 3 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    >
                      {t("dashboard.patientScan")}
                    </Typography>
                  </Box>

                  {loading && !paginatedStudies.length && (
                    <Box sx={{ width: "100%", mb: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}

                  {/* Bulk send toolbar — visible when studies are selected */}
                  {selectedStudiesForBulk.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mb={1.5} p={1.5} sx={{ bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                      <Typography variant="body2" fontWeight={500} flex={1}>
                        {selectedStudiesForBulk.length} {selectedStudiesForBulk.length === 1 ? 'study' : 'studies'} selected
                      </Typography>
                      <Button size="small" onClick={handleClearBulkSelection} sx={{ mr: 1 }}>Clear</Button>
                      <Button
                        size="small" variant="contained" startIcon={<DoneAll />}
                        onClick={handleOpenBulkSend}
                        disabled={selectedStudiesForBulk.length > 10}
                      >
                        {selectedStudiesForBulk.length > 10 ? 'Max 10 studies' : `Bulk Send (${selectedStudiesForBulk.length})`}
                      </Button>
                    </Box>
                  )}

                  {paginatedStudies && paginatedStudies.length > 0 ? (
                    <>
                      <TableContainer
                        sx={{
                          overflowX: "auto",
                          WebkitOverflowScrolling: "touch",
                          maxWidth: "100%",
                          "&::-webkit-scrollbar": {
                            height: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "rgba(0,0,0,0.1)",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "rgba(0,0,0,0.3)",
                            borderRadius: "4px",
                          },
                        }}
                      >
                        <Table size="small">
                          <TableHead sx={{ bgcolor: headerBg }}>
                            <TableRow>
                              <TableCell padding="checkbox" sx={{ borderBottom: cardBorder, py: '5px !important', px: '6px !important' }}>
                                <Checkbox
                                  size="small"
                                  indeterminate={selectedStudiesForBulk.length > 0 && selectedStudiesForBulk.length < paginatedStudies.length}
                                  checked={paginatedStudies.length > 0 && selectedStudiesForBulk.length === paginatedStudies.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedStudiesForBulk(paginatedStudies);
                                    } else {
                                      setSelectedStudiesForBulk([]);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Patient Name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Patient ID
                              </TableCell>
                              <TableCell
                                sx={{
                                  display: { xs: "none", md: "table-cell" },
                                  fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase'
                                }}
                              >
                                Date of Birth
                              </TableCell>
                              <TableCell
                                sx={{
                                  display: { xs: "none", sm: "table-cell" },
                                  fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase'
                                }}
                              >
                                Gender
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Study Description
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Modality
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Study Date
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, color: textColorSecondary, py: '5px !important', px: '8px !important', fontSize: "0.72rem", borderBottom: cardBorder, textTransform: 'uppercase' }}>
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedStudies.map((studyData, studyIndex) => (
                              <StudyRow
                                key={`study-${studyIndex}-${studyData.studyInstanceUID || studyData._id}`}
                                studyData={studyData}
                                index={studyIndex}
                                t={t}
                                isMobile={isMobile}
                                formatPatientName={formatPatientName}
                                onViewImages={handleViewImages}
                                onViewReports={handleViewReports}
                                onCreateSelfReport={handleCreateSelfReport}
                                onRequestDycovue={handleRequestDycovue}
                                onSendEmail={handleSendEmail}
                                onEditMetadata={handleEditMetadata}
                                onViewDetails={handleViewDetails}
                                onViewHistory={handleOpenHistory}
                                onRequestLabTest={(s) => {
                                  setLabTestStudy(s);
                                  setLabTestDialogOpen(true);
                                }}
                                isSelected={selectedStudiesForBulk.some((s) => s._id === studyData._id)}
                                onToggleSelect={handleToggleSelectStudy}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={totalStudyCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage="Rows per page:"
                        labelDisplayedRows={({ from, to, count }) =>
                          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                        sx={{
                          borderTop: `1px solid ${themeObj.palette.divider}`,
                          "& .MuiTablePagination-toolbar": {
                            px: { xs: 1, sm: 2 },
                          },
                          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                            {
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            },
                        }}
                      />
                    </>
                  ) : (
                    <Box textAlign="center" py={8}>
                      <LocalHospital
                        sx={{
                          fontSize: isMobile ? 48 : 64,
                          color: "text.secondary",
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant={isMobile ? "subtitle1" : "h6"}
                        color="textSecondary"
                        gutterBottom
                      >
                        No DICOM studies found
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mb={3}>
                        Upload DICOM files, ZIP archives, or images to get
                        started
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ borderRadius: "12px" }}
                      >
                        Upload First Study
                      </Button>
                    </Box>
                  )}
                </GlassPaper>

              </Container>
            )}

            {selectedStudy && (
              <>
                <PatientDetailDialog
                  open={patientDetailDialogOpen}
                  onClose={() => {
                    setPatientDetailDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  study={selectedStudy}
                  onEditPatient={(patient) => {
                    handleOpenEditPatient(patient);
                  }}
                  onRequestLabTest={(s) => {
                    setLabTestStudy(s);
                    setLabTestDialogOpen(true);
                  }}
                />

                <CreateSelfReport
                  open={selfReportDialogOpen}
                  onClose={() => {
                    setSelfReportDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  study={selectedStudy}
                  onSuccess={() => {
                    setSelfReportDialogOpen(false);
                    loadStudies();
                  }}
                />

                <RequestDycovueReader
                  open={requestDycovueDialogOpen}
                  onClose={() => {
                    setRequestDycovueDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  study={selectedStudy}
                  onSuccess={() => {
                    setRequestDycovueDialogOpen(false);
                    loadStudies();
                  }}
                />

                <SendEmailDialog
                  open={sendEmailDialogOpen}
                  onClose={() => {
                    setSendEmailDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  study={selectedStudy}
                  patientEmail={selectedStudy?.patientEmail || null}
                />

                <EditPatientMetadataDialog
                  open={editMetadataDialogOpen}
                  onClose={() => {
                    setEditMetadataDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  study={selectedStudy}
                  onSuccess={(updatedStudy) => {
                    setEditMetadataDialogOpen(false);
                    fetchScanBoardPage(
                      page,
                      rowsPerPage,
                      debouncedSearch,
                      filters.modality,
                    );
                  }}
                />

                <Dialog
                  open={reportViewerDialogOpen}
                  onClose={() => {
                    setReportViewerDialogOpen(false);
                    setSelectedStudy(null);
                  }}
                  maxWidth={false}
                  PaperProps={{
                    sx: {
                      width: { xs: '95vw', sm: '92vw', md: '860px', lg: '920px' },
                      maxWidth: '940px !important',
                      maxHeight: '88vh',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }
                  }}
                >
                  <DialogTitle sx={{ px: 2, py: 1.25 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Study Reports</Typography>
                      <IconButton
                        onClick={() => {
                          setReportViewerDialogOpen(false);
                          setSelectedStudy(null);
                        }}
                        size="small"
                      >
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </DialogTitle>
                  <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
                    <ReportViewer
                      studyId={selectedStudy._id}
                      study={selectedStudy}
                    />
                  </DialogContent>
                </Dialog>
              </>
            )}

            {/* Patient History Drawer */}
            <PatientHistoryPanel
              open={historyPanel.open}
              onClose={handleCloseHistory}
              patientId={historyPanel.study?.patientId || historyPanel.study?.patientMrn}
              patientName={historyPanel.study?.patientName}
              patientBirthDate={historyPanel.study?.patientBirthDate}
              patientSex={historyPanel.study?.patientSex}
              currentStudyId={historyPanel.study?._id}
            />

            {/* Bulk send dialog — independent of selectedStudy */}
            <SendEmailDialog
              open={bulkSendDialogOpen}
              onClose={() => {
                setBulkSendDialogOpen(false);
                setSelectedStudiesForBulk([]);
              }}
              studies={selectedStudiesForBulk}
            />

            <PatientRegistration
              open={editPatientDialogOpen}
              editPatient={selectedPatientForEdit}
              onClose={() => setEditPatientDialogOpen(false)}
              onSuccess={() => {
                setEditPatientDialogOpen(false);
                fetchPatients(
                  patientsPage,
                  patientsRowsPerPage,
                  patientSearch,
                  patientGender
                );
              }}
              onCreateOrder={(patient) => {
                setEditPatientDialogOpen(false);
                setSelectedPatientForOrder(patient);
                setCurrentView('orders');
              }}
            />

            <Dialog
              open={viewPatientDialogOpen}
              onClose={() => setViewPatientDialogOpen(false)}
              maxWidth={false}
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "14px",
                  p: 0,
                  width: { xs: "95vw", sm: "720px", md: "760px" },
                  maxWidth: "780px !important",
                  maxHeight: "88vh",
                  bgcolor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                  fontFamily: '"Satoshi", sans-serif',
                },
              }}
            >
              <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E2E8F0" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontFamily: '"Satoshi", sans-serif',
                      fontSize: "1.05rem",
                      color: mode === "dark" ? "#FFF" : "#0F172A",
                    }}
                  >
                    Patient Details
                  </Typography>
                  <IconButton
                    onClick={() => setViewPatientDialogOpen(false)}
                    size="small"
                    sx={{ color: mode === "dark" ? "#CBD5E1" : "#64748B" }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent
                sx={{
                  p: 2.5,
                  borderColor: mode === "dark" ? "#334155" : "#E2E8F0",
                }}
              >
                {selectedPatientForView && (
                  <Grid container spacing={1.5} sx={{ mt: 0.2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        PATIENT ID (MRN)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#FFF" : "#1E293B",
                        }}
                      >
                        {selectedPatientForView.mrn ||
                          selectedPatientForView.patientId ||
                          "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        FULL NAME
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#FFF" : "#1E293B",
                        }}
                      >
                        {selectedPatientForView.firstName &&
                        selectedPatientForView.lastName
                          ? `${selectedPatientForView.firstName} ${selectedPatientForView.lastName}`
                          : selectedPatientForView.name ||
                            selectedPatientForView.patientName ||
                            "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        GENDER
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#CBD5E1" : "#475569",
                          fontWeight: 500,
                        }}
                      >
                        {selectedPatientForView.sex === "M" ||
                        selectedPatientForView.patientSex === "M"
                          ? "Male"
                          : selectedPatientForView.sex === "F" ||
                              selectedPatientForView.patientSex === "F"
                            ? "Female"
                            : selectedPatientForView.sex === "O" ||
                                selectedPatientForView.patientSex === "O"
                              ? "Other"
                              : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        AGE / DATE OF BIRTH
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#CBD5E1" : "#475569",
                          fontWeight: 500,
                        }}
                      >
                        {selectedPatientForView.birthDate
                          ? `${formatDicomDate(selectedPatientForView.birthDate)} (${calculateAge(selectedPatientForView.birthDate)})`
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        DATA PROTECTION CONSENT
                      </Typography>
                      <Box sx={{ mt: 0.4 }}>
                        <ConsentBadge patient={selectedPatientForView} isDark={mode === "dark"} />
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        PHONE NUMBER
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#CBD5E1" : "#475569",
                          fontWeight: 500,
                        }}
                      >
                        {selectedPatientForView.telephone ||
                          selectedPatientForView.phone ||
                          selectedPatientForView.contact ||
                          "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        EMAIL ADDRESS
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#CBD5E1" : "#475569",
                          fontWeight: 500,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedPatientForView.email || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 700, display: "block", mb: 0.3, fontFamily: '"Satoshi", sans-serif', color: '#64748B', fontSize: "0.72rem" }}
                      >
                        ADDRESS
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: '"Satoshi", sans-serif',
                          fontSize: "0.85rem",
                          color: mode === "dark" ? "#CBD5E1" : "#475569",
                          fontWeight: 500,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedPatientForView.address || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E2E8F0" }}>
                <Button
                  onClick={() => {
                    setViewPatientDialogOpen(false);
                    handleOpenEditPatient(selectedPatientForView);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    borderColor: "#0D9488",
                    color: "#0D9488",
                    "&:hover": { borderColor: "#00665C", bgcolor: "#F0FDFA" },
                    mr: 1,
                  }}
                >
                  Edit Patient Details
                </Button>
                <Button
                  onClick={() => setViewPatientDialogOpen(false)}
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    bgcolor: "#00665C",
                    color: "#FFF",
                    "&:hover": { bgcolor: "#004D47" },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <EnhancedUploadDialog
              open={uploadDialogOpen}
              onClose={() => {
                setUploadDialogOpen(false);
                setUploadPrefillAccession("");
              }}
              onUpload={async (formData) => {
                await handleUpload(formData);
                await fetchTodayWorkflow();
              }}
              uploading={uploading}
              error={error}
              prefillAccession={uploadPrefillAccession}
            />
            <CreateOrder
              open={showCreateOrder}
              onClose={() => {
                setShowCreateOrder(false);
                setSelectedPatientForOrder(null);
              }}
              onSuccess={(order, targetView) => {
                setShowCreateOrder(false);
                setSelectedPatientForOrder(null);
                if (targetView === 'worklist') setCurrentView('ris_worklist');
                else if (targetView) setCurrentView(targetView);
                else setCurrentView('ris_worklist');
                fetchTodayWorkflow();
              }}
              preselectedPatient={selectedPatientForOrder}
            />
            <PatientRegistration
              open={showRegisterPatient}
              onClose={() => setShowRegisterPatient(false)}
              onSuccess={() => {
                setShowRegisterPatient(false);
                fetchTodayWorkflow();
              }}
              onCreateOrder={(patient) => {
                setShowRegisterPatient(false);
                setSelectedPatientForOrder(patient);
                setCurrentView('orders');
              }}
            />

            <ChangePasswordDialog
              open={changePasswordOpen}
              onClose={() => setChangePasswordOpen(false)}
            />

            {/* Request Lab Test Dialog */}
            {labTestStudy && (
              <RequestLabTestDialog
                open={labTestDialogOpen}
                onClose={() => {
                  setLabTestDialogOpen(false);
                  setLabTestStudy(null);
                }}
                patientId={labTestStudy.patient?.patientId || labTestStudy.patientId}
                patientName={formatPatientName(labTestStudy.patient?.patientName || labTestStudy.patientName)}
                studyUID={labTestStudy.studyInstanceUID}
                isDark={isDark}
              />
            )}
            {/* View Lab Findings Modal */}
            <LabReportModal
              open={labReportModalOpen}
              onClose={() => setLabReportModalOpen(false)}
              resultId={selectedLabReportId}
              patientName={selectedLabPatientName}
              isDark={mode === "dark"}
            />
          </Box>
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default ClinicianDashboard;
